import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Plus, X, Check, Search, TrendingUp, DollarSign, BarChart3, Grid, List, Trash2, Edit3, Zap, ChevronLeft, ChevronRight, CheckSquare, Square, RefreshCw, Activity, CalendarDays, Link2, MoreHorizontal, ArrowUpRight, ArrowDownRight, Clock, Target, Percent, Layers, Filter, Download, Cloud, CloudOff, ChevronDown, LogOut, User, Upload } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, onSnapshot } from 'firebase/firestore';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import LandingPage from './LandingPage';

// ⚠️ FIREBASE CONFIG - Replace with your own credentials from Firebase Console
const firebaseConfig = {
  apiKey: "AIzaSyBcZ-oOVSUd4gw-AybrtwFCnSPXRYv_Y9c",
  authDomain: "wheel-tracker-49f6a.firebaseapp.com",
  projectId: "wheel-tracker-49f6a",
  storageBucket: "wheel-tracker-49f6a.firebasestorage.app",
  messagingSenderId: "236333206777",
  appId: "1:236333206777:web:ab218fc429e58bdb9ea628"
};

// Initialize Firebase
let app = null;
let db = null;
let auth = null;
let googleProvider = null;
let firebaseEnabled = false;

try {
  if (firebaseConfig.apiKey !== "YOUR_API_KEY") {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    firebaseEnabled = true;
  }
} catch (error) {
  console.log('Firebase not configured, using localStorage only');
}

// Premium Color System
const colors = {
  // Backgrounds
  bgPrimary: '#0a0e17',
  bgSecondary: '#111827',
  bgTertiary: '#1a2332',
  bgCard: 'rgba(26, 35, 50, 0.7)',
  bgCardHover: 'rgba(34, 46, 64, 0.8)',
  bgElevated: 'rgba(30, 41, 59, 0.9)',
  
  // Borders
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  borderHover: 'rgba(148, 163, 184, 0.2)',
  
  // Text
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  textTertiary: '#64748b',
  textMuted: '#475569',
  
  // Accents
  accent: '#06b6d4',
  accentHover: '#22d3ee',
  accentMuted: 'rgba(6, 182, 212, 0.15)',
  
  // Semantic
  profit: '#10b981',
  profitMuted: 'rgba(16, 185, 129, 0.15)',
  loss: '#f43f5e',
  lossMuted: 'rgba(244, 63, 94, 0.15)',
  warning: '#f59e0b',
  warningMuted: 'rgba(245, 158, 11, 0.15)',
  info: '#3b82f6',
  infoMuted: 'rgba(59, 130, 246, 0.15)',
  purple: '#a855f7',
  purpleMuted: 'rgba(168, 85, 247, 0.15)',
};

const statusConfig = {
  open: { color: colors.accent, bg: colors.accentMuted, label: 'Open' },
  closed: { color: colors.purple, bg: colors.purpleMuted, label: 'Closed' },
  expired: { color: colors.profit, bg: colors.profitMuted, label: 'Expired' },
  assigned: { color: colors.warning, bg: colors.warningMuted, label: 'Assigned' },
  sold: { color: colors.profit, bg: colors.profitMuted, label: 'Sold' },
  rolled: { color: colors.purple, bg: colors.purpleMuted, label: 'Rolled' },
};

const WheelTracker = () => {
  const [trades, setTrades] = useState([]);
  const [view, setView] = useState('grouped');
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [favorites, setFavorites] = useState(['QQQ', 'SPY', 'SOXL', 'NVDA', 'AAPL']);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('1M');
  const [selectedTrades, setSelectedTrades] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showRollModal, setShowRollModal] = useState(null);
  const quickEntryRef = useRef(null);
  const fileInputRef = useRef(null);
  const importFileRef = useRef(null);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]); // Parsed CSV rows
  const [importSelected, setImportSelected] = useState(new Set()); // Selected row indices
  const [importBroker, setImportBroker] = useState('auto'); // auto, schwab, fidelity, generic

  // Authentication state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [quickForm, setQuickForm] = useState({ ticker: '', type: 'CSP', strike: '', premium: '', contracts: 1 });
  const [form, setForm] = useState({ ticker: '', type: 'CSP', strike: '', premium: '', contracts: 1, openDate: '', expiration: '', entryFees: '', notes: '', rolledFrom: null });
  const [shareForm, setShareForm] = useState({ ticker: '', quantity: 100, costBasis: '', openDate: '', entryFees: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ exitPremium: '', exitFees: '' });
  const [bulkText, setBulkText] = useState('');
  const [rollForm, setRollForm] = useState({ strike: '', premium: '', expiration: '', contracts: 1 });
  const [dashboardTab, setDashboardTab] = useState('overview'); // overview, daily, weekly

  const today = new Date().toISOString().split('T')[0];
  const isInitialMount = useRef(true);
  const [cloudStatus, setCloudStatus] = useState(firebaseEnabled ? 'connecting' : 'disabled');
  const unsubscribeRef = useRef(null);
  const isLoadingFromFirebase = useRef(false); // Prevent save loop
  const hasLoadedFromFirebase = useRef(false); // Track if we've received Firebase data

  // Google Sign In
  const handleSignIn = async () => {
    if (!firebaseEnabled || !auth || !googleProvider) {
      alert('Firebase not configured. Please add your Firebase config.');
      return;
    }
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Sign in failed: ' + error.message);
    }
  };

  // Sign Out
  const handleSignOut = async () => {
    if (!auth) return;
    try {
      await signOut(auth);
      setTrades([]);
      setFavorites(['QQQ', 'SPY', 'SOXL', 'NVDA', 'AAPL']);
      setCloudStatus('disabled');
      hasLoadedFromFirebase.current = false;
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    if (!firebaseEnabled || !auth) {
      setAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      if (!currentUser) {
        // User signed out - clean up listener
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        setCloudStatus('disabled');
        hasLoadedFromFirebase.current = false;
      }
    });

    return () => unsubscribe();
  }, []);

  // Load data and set up Firebase listener when user is authenticated
  useEffect(() => {
    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Reset flags when user changes
    isInitialMount.current = true;
    hasLoadedFromFirebase.current = false;

    // Set up Firebase real-time listener if user is authenticated
    // IMPORTANT: Firebase is the source of truth - don't load localStorage first when user is signed in
    if (firebaseEnabled && db && user) {
      setCloudStatus('connecting');
      const docRef = doc(db, 'wheelTracker', user.uid);
      
      unsubscribeRef.current = onSnapshot(docRef, (docSnap) => {
        isLoadingFromFirebase.current = true; // Flag to prevent save loop
        hasLoadedFromFirebase.current = true; // Mark that we've received Firebase data
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.trades && Array.isArray(data.trades)) {
            setTrades(data.trades);
            localStorage.setItem('wheelTradesV5', JSON.stringify(data.trades));
          } else {
            // Document exists but no trades array - start fresh
            setTrades([]);
          }
          if (data.favorites && Array.isArray(data.favorites)) {
            setFavorites(data.favorites);
            localStorage.setItem('wheelFavorites', JSON.stringify(data.favorites));
          }
        } else {
          // No document exists yet - check localStorage for migration
          const saved = localStorage.getItem('wheelTradesV5');
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              if (Array.isArray(parsed) && parsed.length > 0) {
                // Migrate localStorage data to Firebase
                setTrades(parsed);
                console.log('Migrating localStorage data to Firebase');
              }
            } catch (e) {
              console.error('Error parsing localStorage:', e);
            }
          }
        }
        
        setCloudStatus('synced');
        setTimeout(() => { 
          isInitialMount.current = false; 
          isLoadingFromFirebase.current = false;
        }, 300);
      }, (error) => {
        console.error('Firebase listener error:', error);
        setCloudStatus('error');
        hasLoadedFromFirebase.current = true; // Still mark as loaded so app works offline
        
        // Fall back to localStorage on error
        const saved = localStorage.getItem('wheelTradesV5');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) setTrades(parsed);
          } catch (e) {
            console.error('Error loading from localStorage:', e);
          }
        }
        
        setTimeout(() => { 
          isInitialMount.current = false;
          isLoadingFromFirebase.current = false;
        }, 300);
      });
    } else {
      // Not authenticated - use localStorage only
      const saved = localStorage.getItem('wheelTradesV5');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) setTrades(parsed);
        } catch (e) {
          console.error('Error loading trades from localStorage:', e);
        }
      }
      const savedFavs = localStorage.getItem('wheelFavorites');
      if (savedFavs) {
        try {
          const parsed = JSON.parse(savedFavs);
          if (Array.isArray(parsed)) setFavorites(parsed);
        } catch (e) {
          console.error('Error loading favorites from localStorage:', e);
        }
      }
      setTimeout(() => { isInitialMount.current = false; }, 100);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [user]);

  // Save to Firebase and localStorage when trades change
  useEffect(() => {
    if (isInitialMount.current) return;
    if (isLoadingFromFirebase.current) return; // Don't save if we just loaded from Firebase
    
    // CRITICAL: Don't save to Firebase until we've loaded from Firebase first
    // This prevents overwriting cloud data with empty local data
    if (firebaseEnabled && user && !hasLoadedFromFirebase.current) {
      console.log('Waiting for Firebase to load before saving...');
      return;
    }
    
    // Always save to localStorage as cache
    localStorage.setItem('wheelTradesV5', JSON.stringify(trades));
    
    // Save to Firebase if user is authenticated
    if (firebaseEnabled && db && user) {
      setCloudStatus('syncing');
      const docRef = doc(db, 'wheelTracker', user.uid);
      setDoc(docRef, { 
        trades, 
        favorites,
        lastUpdated: new Date().toISOString(),
        userEmail: user.email
      }, { merge: true })
        .then(() => setCloudStatus('synced'))
        .catch((error) => {
          console.error('Firebase save error:', error);
          setCloudStatus('error');
        });
    }
  }, [trades, user]);

  // Save favorites
  useEffect(() => {
    if (isInitialMount.current) return;
    if (isLoadingFromFirebase.current) return; // Don't save if we just loaded from Firebase
    
    localStorage.setItem('wheelFavorites', JSON.stringify(favorites));
    
    if (firebaseEnabled && db && user) {
      const docRef = doc(db, 'wheelTracker', user.uid);
      setDoc(docRef, { favorites }, { merge: true }).catch(console.error);
    }
  }, [favorites, user]);

  // Export trades to JSON file
  const exportTrades = () => {
    const data = {
      trades,
      favorites,
      exportedAt: new Date().toISOString(),
      version: 'v5'
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wheel-tracker-backup-${today}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'n' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setShowQuickEntry(true); setTimeout(() => quickEntryRef.current?.focus(), 100); }
      if (e.key === 'Escape') { setShowQuickEntry(false); setShowModal(null); setSelectMode(false); setSelectedTrades(new Set()); setShowRollModal(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Core functions
  const handleQuickEntry = (e) => {
    e.preventDefault();
    if (!quickForm.ticker || !quickForm.strike || !quickForm.premium) return;
    const trade = { id: Date.now(), ...quickForm, ticker: quickForm.ticker.toUpperCase(), strike: parseFloat(quickForm.strike), premium: parseFloat(quickForm.premium), contracts: parseInt(quickForm.contracts), openDate: today, entryFees: 0, status: 'open', tradeType: 'option' };
    setTrades([trade, ...trades]);
    setQuickForm({ ticker: '', type: 'CSP', strike: '', premium: '', contracts: 1 });
    if (!favorites.includes(trade.ticker)) setFavorites([...favorites.slice(0, 5), trade.ticker].slice(0, 6));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.type === 'SHARES') {
      // Handle as shares
      const share = { 
        id: editingId || Date.now(), 
        ticker: form.ticker.toUpperCase(), 
        tradeType: 'shares',
        quantity: parseInt(form.contracts), 
        costBasis: parseFloat(form.strike), 
        openDate: form.openDate || today, 
        entryFees: parseFloat(form.entryFees) || 0, 
        status: 'open',
        notes: form.notes || ''
      };
      if (editingId) setTrades(trades.map(t => t.id === editingId ? { ...t, ...share } : t));
      else setTrades([share, ...trades]);
    } else {
      // Handle as option
      const trade = { id: editingId || Date.now(), ...form, ticker: form.ticker.toUpperCase(), strike: parseFloat(form.strike), premium: parseFloat(form.premium), contracts: parseInt(form.contracts), openDate: form.openDate || today, entryFees: parseFloat(form.entryFees) || 0, status: 'open', tradeType: 'option', rolledFrom: form.rolledFrom || null };
      if (editingId) setTrades(trades.map(t => t.id === editingId ? { ...t, ...trade } : t));
      else setTrades([trade, ...trades]);
    }
    closeModal();
  };

  const handleShareSubmit = (e) => {
    e.preventDefault();
    const share = { id: editingId || Date.now(), ...shareForm, ticker: shareForm.ticker.toUpperCase(), quantity: parseInt(shareForm.quantity), costBasis: parseFloat(shareForm.costBasis), openDate: shareForm.openDate || today, entryFees: parseFloat(shareForm.entryFees) || 0, status: 'open', tradeType: 'shares' };
    if (editingId) setTrades(trades.map(t => t.id === editingId ? { ...t, ...share } : t));
    else setTrades([share, ...trades]);
    closeModal();
  };

  // Helper to get appropriate close date (use expiration if in the past, otherwise today)
  const getCloseDate = (trade) => {
    if (!trade.expiration) return today;
    const expDate = trade.expiration;
    return expDate < today ? expDate : today;
  };

  const handleClose = () => {
    const trade = modalData;
    const exitPremium = parseFloat(closeForm.exitPremium) || 0;
    const exitFees = parseFloat(closeForm.exitFees) || 0;
    const closeDate = getCloseDate(trade);
    if (trade.tradeType === 'shares') setTrades(trades.map(t => t.id === trade.id ? { ...t, status: 'sold', sellPrice: exitPremium, exitFees, closeDate } : t));
    else setTrades(trades.map(t => t.id === trade.id ? { ...t, status: 'closed', exitPremium, exitFees, closeDate } : t));
    closeModal();
  };

  // For expired trades, always use the expiration date
  const handleExpire = (trade) => {
    const closeDate = trade.expiration || today;
    setTrades(trades.map(t => t.id === trade.id ? { ...t, status: 'expired', exitPremium: 0, exitFees: 0, closeDate } : t));
  };

  const handleAssign = () => {
    const trade = modalData;
    const fees = parseFloat(closeForm.exitFees) || 0;
    const closeDate = getCloseDate(trade);
    setTrades(prev => {
      const updated = prev.map(t => t.id === trade.id ? { ...t, status: 'assigned', exitFees: fees, closeDate } : t);
      const share = { id: Date.now(), ticker: trade.ticker, tradeType: 'shares', quantity: trade.contracts * 100, costBasis: trade.strike, openDate: closeDate, entryFees: fees, status: 'open', fromTradeId: trade.id, notes: `Assigned from ${trade.type} @ $${trade.strike}` };
      return [share, ...updated];
    });
    closeModal();
  };

  const handleRoll = () => {
    const originalTrade = showRollModal;
    const exitPremium = parseFloat(closeForm.exitPremium) || 0;
    const exitFees = parseFloat(closeForm.exitFees) || 0;
    const closeDate = getCloseDate(originalTrade);
    const updatedTrades = trades.map(t => t.id === originalTrade.id ? { ...t, status: 'rolled', exitPremium, exitFees, closeDate } : t);
    const newTrade = { id: Date.now(), ticker: originalTrade.ticker, type: originalTrade.type, strike: parseFloat(rollForm.strike), premium: parseFloat(rollForm.premium), contracts: parseInt(rollForm.contracts) || originalTrade.contracts, openDate: closeDate, expiration: rollForm.expiration || '', entryFees: 0, status: 'open', tradeType: 'option', rolledFrom: originalTrade.id, rollChain: [...(originalTrade.rollChain || []), originalTrade.id] };
    setTrades([newTrade, ...updatedTrades]);
    setShowRollModal(null);
    setRollForm({ strike: '', premium: '', expiration: '', contracts: 1 });
    setCloseForm({ exitPremium: '', exitFees: '' });
  };

  const handleBulkUpload = () => {
    try {
      const lines = bulkText.trim().split('\n').filter(l => l.trim());
      const newTrades = [];
      lines.forEach((line, i) => {
        if (i === 0 && line.toLowerCase().includes('ticker')) return;
        const [ticker, type, strike, premium, contracts, openDate, entryFees, status, exitPremium, exitFees, notes] = line.split(',').map(p => p.trim());
        if (!ticker || !strike) return;
        const isShares = type?.toUpperCase() === 'SHARES';
        newTrades.push({ id: Date.now() + i, ticker: ticker.toUpperCase(), tradeType: isShares ? 'shares' : 'option', type: isShares ? null : (type?.toUpperCase() === 'CC' || type?.toLowerCase().includes('call') ? 'CC' : 'CSP'), strike: parseFloat(strike), premium: isShares ? null : parseFloat(premium), contracts: isShares ? null : parseInt(contracts) || 1, quantity: isShares ? (parseInt(contracts) || 1) * 100 : null, costBasis: isShares ? parseFloat(strike) : null, openDate: openDate || today, entryFees: parseFloat(entryFees) || 0, status: status?.toLowerCase() || 'open', exitPremium: parseFloat(exitPremium) || 0, exitFees: parseFloat(exitFees) || 0, sellPrice: isShares ? parseFloat(exitPremium) || null : null, closeDate: status && status.toLowerCase() !== 'open' ? openDate : null, notes: notes || '' });
      });
      setTrades([...newTrades.reverse(), ...trades]);
      closeModal();
    } catch (err) { alert('Import error: ' + err.message); }
  };

  // ===== CSV IMPORT FUNCTIONS =====
  
  // Parse CSV text into array of arrays
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    return lines.map(line => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let char of line) {
        if (char === '"') inQuotes = !inQuotes;
        else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else current += char;
      }
      result.push(current.trim());
      return result;
    });
  };

  // Detect broker from CSV headers
  const detectBroker = (headers) => {
    const h = headers.map(s => s.toLowerCase());
    if (h.includes('action') && h.includes('symbol') && h.includes('description')) return 'schwab';
    if (h.includes('run date') && h.includes('action') && h.includes('symbol')) return 'fidelity';
    if (h.includes('activity date') && h.includes('instrument')) return 'tda';
    return 'generic';
  };

  // Parse option symbol like "QQQ 01/24/2025 600.00 P" or "QQQ250124P600"
  const parseOptionSymbol = (symbol, description = '') => {
    // Try to parse from description first (more readable)
    // Format: "PUT QQQ $600 1/24/25" or "CALL QQQ $600 1/24/25"
    const descMatch = description.match(/(PUT|CALL)\s+(\w+)\s+\$?([\d.]+)\s+(\d+\/\d+\/\d+)/i);
    if (descMatch) {
      const type = descMatch[1].toUpperCase() === 'PUT' ? 'CSP' : 'CC';
      const ticker = descMatch[2].toUpperCase();
      const strike = parseFloat(descMatch[3]);
      const expParts = descMatch[4].split('/');
      const exp = `20${expParts[2]}-${expParts[0].padStart(2,'0')}-${expParts[1].padStart(2,'0')}`;
      return { ticker, type, strike, expiration: exp };
    }
    
    // Try OCC format: QQQ250124P00600000
    const occMatch = symbol.match(/(\w+)(\d{2})(\d{2})(\d{2})([PC])(\d{8})/);
    if (occMatch) {
      const ticker = occMatch[1];
      const exp = `20${occMatch[2]}-${occMatch[3]}-${occMatch[4]}`;
      const type = occMatch[5] === 'P' ? 'CSP' : 'CC';
      const strike = parseInt(occMatch[6]) / 1000;
      return { ticker, type, strike, expiration: exp };
    }
    
    // Try format: "QQQ 01/24/2025 600.00 P"
    const spaceMatch = symbol.match(/(\w+)\s+(\d+\/\d+\/\d+)\s+([\d.]+)\s+([PC])/);
    if (spaceMatch) {
      const ticker = spaceMatch[1];
      const dateParts = spaceMatch[2].split('/');
      const exp = `${dateParts[2]}-${dateParts[0].padStart(2,'0')}-${dateParts[1].padStart(2,'0')}`;
      const strike = parseFloat(spaceMatch[3]);
      const type = spaceMatch[4] === 'P' ? 'CSP' : 'CC';
      return { ticker, type, strike, expiration: exp };
    }
    
    return null;
  };

  // Parse a row based on broker format
  const parseTradeRow = (row, headers, broker) => {
    const get = (name) => {
      const idx = headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()));
      return idx >= 0 ? row[idx] : '';
    };

    let action, symbol, description, quantity, price, date, fees;

    if (broker === 'schwab') {
      action = get('action');
      symbol = get('symbol');
      description = get('description');
      quantity = get('quantity');
      price = get('price');
      date = get('date');
      fees = get('fees') || get('commission');
    } else if (broker === 'fidelity') {
      action = get('action');
      symbol = get('symbol');
      description = get('description');
      quantity = get('quantity');
      price = get('price');
      date = get('run date') || get('date');
      fees = get('commission');
    } else {
      // Generic - try common column names
      action = get('action') || get('type') || get('side');
      symbol = get('symbol') || get('ticker');
      description = get('description') || get('desc');
      quantity = get('quantity') || get('qty') || get('contracts');
      price = get('price') || get('premium');
      date = get('date') || get('trade date');
      fees = get('fees') || get('commission') || '0';
    }

    // Normalize action
    const actionLower = action.toLowerCase();
    let tradeAction = 'unknown';
    if (actionLower.includes('sell to open') || actionLower.includes('sto')) tradeAction = 'sell_to_open';
    else if (actionLower.includes('buy to close') || actionLower.includes('btc')) tradeAction = 'buy_to_close';
    else if (actionLower.includes('buy to open') || actionLower.includes('bto')) tradeAction = 'buy_to_open';
    else if (actionLower.includes('sell to close') || actionLower.includes('stc')) tradeAction = 'sell_to_close';
    else if (actionLower.includes('assigned')) tradeAction = 'assigned';
    else if (actionLower.includes('expired')) tradeAction = 'expired';
    else if (actionLower.includes('buy') && !actionLower.includes('option')) tradeAction = 'buy_stock';
    else if (actionLower.includes('sell') && !actionLower.includes('option')) tradeAction = 'sell_stock';

    // Parse option details
    const optionInfo = parseOptionSymbol(symbol, description);
    
    // Parse date
    let parsedDate = today;
    if (date) {
      const dateMatch = date.match(/(\d+)\/(\d+)\/(\d+)/);
      if (dateMatch) {
        const year = dateMatch[3].length === 2 ? '20' + dateMatch[3] : dateMatch[3];
        parsedDate = `${year}-${dateMatch[1].padStart(2,'0')}-${dateMatch[2].padStart(2,'0')}`;
      }
    }

    return {
      action: tradeAction,
      symbol: optionInfo?.ticker || symbol.split(' ')[0].toUpperCase(),
      type: optionInfo?.type || null,
      strike: optionInfo?.strike || null,
      expiration: optionInfo?.expiration || null,
      quantity: Math.abs(parseInt(quantity)) || 1,
      price: Math.abs(parseFloat(price)) || 0,
      date: parsedDate,
      fees: Math.abs(parseFloat(fees)) || 0,
      description: description,
      raw: row.join(', ')
    };
  };

  // Find matching open trade for closing
  const findMatchingTrade = (parsed) => {
    if (!parsed.type || !parsed.strike) return null;
    
    return trades.find(t => 
      t.status === 'open' &&
      t.tradeType === 'option' &&
      t.ticker === parsed.symbol &&
      t.type === parsed.type &&
      Math.abs(t.strike - parsed.strike) < 0.01 &&
      (!parsed.expiration || t.expiration === parsed.expiration)
    );
  };

  // Find shares to link CC to
  const findSharesForCC = (ticker) => {
    return trades.find(t => 
      t.status === 'open' &&
      t.tradeType === 'shares' &&
      t.ticker === ticker
    );
  };

  // Parse IBKR HTML format
  const parseIBKRHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const trades = [];
    
    // Find all summary rows (these are the aggregated trades)
    const summaryRows = doc.querySelectorAll('tr.row-summary');
    
    summaryRows.forEach((row, idx) => {
      const cells = row.querySelectorAll('td');
      if (cells.length < 10) return;
      
      // Extract data from cells - IBKR HTML format:
      // cells[0]: Account ID
      // cells[1]: Symbol (colspan=2, e.g., "QQQ 21JAN26 625 C")
      // cells[2]: Trade Date/Time
      // cells[3]: Settle Date
      // cells[4]: Exchange
      // cells[5]: Type (BUY/SELL)
      // cells[6]: Quantity
      // cells[7]: Price
      // cells[8]: Proceeds
      // cells[9]: Commission
      // cells[10]: Fee
      // cells[11]: Order Type
      // cells[12]: Code (O=Open, C=Close, P=Partial)
      
      const symbolCell = cells[1]?.textContent?.trim() || '';
      const dateTime = cells[2]?.textContent?.trim() || '';
      const buySell = cells[5]?.textContent?.trim() || '';
      const quantity = Math.abs(parseInt(cells[6]?.textContent?.trim()) || 1);
      const price = Math.abs(parseFloat(cells[7]?.textContent?.trim()) || 0);
      const commission = Math.abs(parseFloat(cells[9]?.textContent?.trim()) || 0);
      const fee = Math.abs(parseFloat(cells[10]?.textContent?.trim()) || 0);
      const code = cells[12]?.textContent?.trim() || '';
      
      // Parse symbol: "QQQ 21JAN26 625 C" or "SOXL 30JAN26 55 P"
      const symbolMatch = symbolCell.match(/(\w+)\s+(\d{2})([A-Z]{3})(\d{2})\s+([\d.]+)\s+([CP])/i);
      if (!symbolMatch) return;
      
      const ticker = symbolMatch[1].toUpperCase();
      const day = symbolMatch[2];
      const monthStr = symbolMatch[3].toUpperCase();
      const year = '20' + symbolMatch[4];
      const strike = parseFloat(symbolMatch[5]);
      const optionType = symbolMatch[6].toUpperCase() === 'P' ? 'CSP' : 'CC';
      
      // Convert month
      const months = { JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06', JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12' };
      const month = months[monthStr] || '01';
      const expiration = `${year}-${month}-${day}`;
      
      // Parse trade date
      const dateMatch = dateTime.match(/(\d{4})-(\d{2})-(\d{2})/);
      const tradeDate = dateMatch ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}` : today;
      
      // Determine action based on BUY/SELL and Code
      // O = Open, C = Close, P = Partial
      let action = 'unknown';
      const isSell = buySell.toUpperCase() === 'SELL';
      const isOpen = code.includes('O');
      const isClose = code.includes('C');
      
      if (isSell && isOpen) action = 'sell_to_open';      // Sell to Open (new position)
      else if (!isSell && isClose) action = 'buy_to_close'; // Buy to Close (closing position)
      else if (isSell && isClose) action = 'sell_to_close'; // Sell to Close (rare for CSPs)
      else if (!isSell && isOpen) action = 'buy_to_open';   // Buy to Open (buying options)
      
      trades.push({
        action,
        symbol: ticker,
        type: optionType,
        strike,
        expiration,
        quantity,
        price,
        date: tradeDate,
        fees: commission + fee,
        description: symbolCell,
        raw: symbolCell,
        _index: idx
      });
    });
    
    return trades;
  };

  // Handle file upload (CSV or HTML)
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        let parsed = [];
        
        // Detect file type
        const isHtml = file.name.endsWith('.html') || file.name.endsWith('.htm') || text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html');
        
        if (isHtml) {
          // Parse as IBKR HTML
          parsed = parseIBKRHtml(text);
        } else {
          // Parse as CSV
          const rows = parseCSV(text);
          if (rows.length < 2) { alert('No data found in file'); return; }

          const headers = rows[0];
          const broker = importBroker === 'auto' ? detectBroker(headers) : importBroker;
          
          parsed = rows.slice(1)
            .map((row, idx) => ({ ...parseTradeRow(row, headers, broker), _index: idx }))
            .filter(p => p.action !== 'unknown' && p.symbol);
        }
        
        if (parsed.length === 0) {
          alert('No trades found in file. Make sure it\'s a supported format.');
          return;
        }

        // Find matching trades for closing actions
        parsed.forEach(p => {
          if (p.action === 'buy_to_close' || p.action === 'assigned' || p.action === 'expired') {
            p.matchingTrade = findMatchingTrade(p);
          }
        });

        setImportData(parsed);
        setImportSelected(new Set(parsed.map((_, i) => i)));
        setShowImportModal(true);
      } catch (err) {
        alert('Error parsing file: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Import selected trades
  const handleImportConfirm = () => {
    const toImport = importData.filter((_, i) => importSelected.has(i));
    const newTrades = [];
    const updatedTradeIds = new Set();

    toImport.forEach((p, idx) => {
      if (p.action === 'sell_to_open') {
        // New option position
        newTrades.push({
          id: Date.now() + idx,
          ticker: p.symbol,
          tradeType: 'option',
          type: p.type || 'CSP',
          strike: p.strike || 0,
          premium: p.price,
          contracts: p.quantity,
          openDate: p.date,
          expiration: p.expiration || '',
          entryFees: p.fees,
          status: 'open',
          exitPremium: 0,
          exitFees: 0,
          notes: ''
        });
      } else if (p.action === 'buy_to_close' && p.matchingTrade) {
        // Close existing position
        setTrades(prev => prev.map(t => 
          t.id === p.matchingTrade.id 
            ? { ...t, status: 'closed', exitPremium: p.price, exitFees: p.fees, closeDate: p.date }
            : t
        ));
        updatedTradeIds.add(p.matchingTrade.id);
      } else if (p.action === 'assigned' && p.matchingTrade) {
        // Mark as assigned and create shares
        const shareId = Date.now() + idx + 1000;
        setTrades(prev => {
          const updated = prev.map(t => 
            t.id === p.matchingTrade.id 
              ? { ...t, status: 'assigned', exitFees: p.fees, closeDate: p.date }
              : t
          );
          const shares = {
            id: shareId,
            ticker: p.symbol,
            tradeType: 'shares',
            quantity: p.matchingTrade.contracts * 100,
            costBasis: p.matchingTrade.strike,
            openDate: p.date,
            entryFees: p.fees,
            status: 'open',
            fromTradeId: p.matchingTrade.id,
            notes: `Assigned from ${p.matchingTrade.type} @ $${p.matchingTrade.strike}`
          };
          return [shares, ...updated];
        });
        updatedTradeIds.add(p.matchingTrade.id);
      } else if (p.action === 'expired' && p.matchingTrade) {
        // Mark as expired
        setTrades(prev => prev.map(t => 
          t.id === p.matchingTrade.id 
            ? { ...t, status: 'expired', exitPremium: 0, exitFees: 0, closeDate: p.expiration || p.date }
            : t
        ));
        updatedTradeIds.add(p.matchingTrade.id);
      } else if (p.action === 'buy_stock') {
        // New shares position
        newTrades.push({
          id: Date.now() + idx,
          ticker: p.symbol,
          tradeType: 'shares',
          quantity: p.quantity,
          costBasis: p.price,
          openDate: p.date,
          entryFees: p.fees,
          status: 'open',
          notes: ''
        });
      } else if (p.action === 'sell_stock') {
        // Sell shares - find matching shares
        const shares = trades.find(t => t.tradeType === 'shares' && t.ticker === p.symbol && t.status === 'open');
        if (shares) {
          setTrades(prev => prev.map(t => 
            t.id === shares.id 
              ? { ...t, status: 'sold', sellPrice: p.price, exitFees: p.fees, closeDate: p.date }
              : t
          ));
          updatedTradeIds.add(shares.id);
        }
      }
    });

    if (newTrades.length > 0) {
      setTrades(prev => [...newTrades, ...prev]);
    }

    setShowImportModal(false);
    setImportData([]);
    setImportSelected(new Set());
  };

  const toggleImportRow = (idx) => {
    const next = new Set(importSelected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setImportSelected(next);
  };

  const toggleAllImportRows = () => {
    if (importSelected.size === importData.length) {
      setImportSelected(new Set());
    } else {
      setImportSelected(new Set(importData.map((_, i) => i)));
    }
  };

  const closeModal = () => { setShowModal(null); setModalData(null); setEditingId(null); setForm({ ticker: '', type: 'CSP', strike: '', premium: '', contracts: 1, openDate: '', expiration: '', entryFees: '', notes: '', rolledFrom: null }); setShareForm({ ticker: '', quantity: 100, costBasis: '', openDate: '', entryFees: '', notes: '' }); setCloseForm({ exitPremium: '', exitFees: '' }); setBulkText(''); };

  const openEdit = (trade) => {
    if (trade.tradeType === 'shares') { setShareForm({ ticker: trade.ticker, quantity: trade.quantity, costBasis: trade.costBasis.toString(), openDate: trade.openDate, entryFees: trade.entryFees?.toString() || '', notes: trade.notes || '' }); setEditingId(trade.id); setShowModal('shares'); }
    else { setForm({ ticker: trade.ticker, type: trade.type, strike: trade.strike.toString(), premium: trade.premium.toString(), contracts: trade.contracts, openDate: trade.openDate, expiration: trade.expiration || '', entryFees: trade.entryFees?.toString() || '', notes: trade.notes || '', rolledFrom: trade.rolledFrom }); setEditingId(trade.id); setShowModal('trade'); }
  };

  const duplicateTrade = (trade) => { const newTrade = { ...trade, id: Date.now(), status: 'open', openDate: today, closeDate: null, exitPremium: null, exitFees: null, rolledFrom: null, rollChain: null }; setTrades([newTrade, ...trades]); };
  const deleteTrade = (id) => { setTrades(trades.filter(t => t.id !== id)); setConfirmDelete(null); };
  const toggleSelectTrade = (id) => { const n = new Set(selectedTrades); if (n.has(id)) n.delete(id); else n.add(id); setSelectedTrades(n); };
  const selectAllVisible = () => { const ids = filteredTrades.map(t => t.id); if (ids.every(id => selectedTrades.has(id))) setSelectedTrades(new Set()); else setSelectedTrades(new Set(ids)); };
  const handleBulkDelete = () => { setTrades(trades.filter(t => !selectedTrades.has(t.id))); setSelectedTrades(new Set()); setSelectMode(false); setConfirmDelete(null); };

  // Calculations - Premium-Based P/L Method
  // Options: P/L = premium collected (attributed to open date), minus exit cost if closed
  // Shares: P/L only when sold
  const calcPL = (t) => { 
    if (t.tradeType === 'shares') { 
      if (t.status !== 'sold') return null; 
      return (t.sellPrice - t.costBasis) * t.quantity - (t.entryFees || 0) - (t.exitFees || 0); 
    } 
    // Options: always show P/L (even open = assume expires worthless)
    const mult = t.contracts * 100; 
    const grossPremium = (t.premium || 0) * mult;
    const exitCost = (t.exitPremium || 0) * mult;
    const fees = (t.entryFees || 0) + (t.exitFees || 0);
    return grossPremium - exitCost - fees;
  };
  const previewPL = (trade, exitPremium) => { if (trade.tradeType === 'shares') return ((parseFloat(exitPremium) || 0) - trade.costBasis) * trade.quantity; return (trade.premium - (parseFloat(exitPremium) || 0)) * trade.contracts * 100; };
  
  const getDTE = (expiration) => {
    if (!expiration) return null;
    const exp = new Date(expiration + 'T23:59:59');
    const now = new Date();
    return Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  };

  const getRollChain = (trade) => {
    const chain = [];
    let current = trade;
    while (current.rolledFrom) {
      const prev = trades.find(t => t.id === current.rolledFrom);
      if (prev) { chain.unshift(prev); current = prev; }
      else break;
    }
    chain.push(trade);
    let nextRoll = trades.find(t => t.rolledFrom === trade.id);
    while (nextRoll) { chain.push(nextRoll); nextRoll = trades.find(t => t.rolledFrom === nextRoll.id); }
    return chain;
  };

  const getRollChainPremium = (trade) => {
    const chain = getRollChain(trade);
    return chain.reduce((sum, t) => {
      const premium = t.premium * t.contracts * 100;
      const exitCost = (t.exitPremium || 0) * t.contracts * 100;
      return sum + premium - exitCost - (t.entryFees || 0) - (t.exitFees || 0);
    }, 0);
  };

  const filteredTrades = useMemo(() => {
    let result = trades;
    if (filter === 'open') result = result.filter(t => t.status === 'open');
    else if (filter === 'closed') result = result.filter(t => ['closed', 'expired', 'assigned', 'sold', 'rolled'].includes(t.status));
    else if (filter === 'shares') result = result.filter(t => t.tradeType === 'shares');
    else if (filter === 'options') result = result.filter(t => t.tradeType === 'option');
    if (search) { const s = search.toUpperCase(); result = result.filter(t => t.ticker.includes(s)); }
    result = [...result].sort((a, b) => { let aVal, bVal; if (sortBy === 'date') { aVal = a.closeDate || a.openDate; bVal = b.closeDate || b.openDate; } else if (sortBy === 'ticker') { aVal = a.ticker; bVal = b.ticker; } else if (sortBy === 'pl') { aVal = calcPL(a) || 0; bVal = calcPL(b) || 0; } if (sortDir === 'asc') return aVal > bVal ? 1 : -1; return aVal < bVal ? 1 : -1; });
    return result;
  }, [trades, filter, search, sortBy, sortDir]);

  // Grouped trades by ticker for grouped view
  const groupedTrades = useMemo(() => {
    const groups = {};
    
    filteredTrades.forEach(t => {
      if (!groups[t.ticker]) {
        groups[t.ticker] = {
          ticker: t.ticker,
          trades: [],
          totalPending: 0,
          totalExposure: 0,
          positionCount: 0
        };
      }
      groups[t.ticker].trades.push(t);
      groups[t.ticker].positionCount++;
      
      // Calculate pending premium (for open options)
      if (t.status === 'open' && t.tradeType === 'option') {
        groups[t.ticker].totalPending += (t.premium || 0) * (t.contracts || 1) * 100;
      }
      
      // Calculate exposure (capital at risk)
      if (t.status === 'open') {
        if (t.tradeType === 'shares') {
          groups[t.ticker].totalExposure += (t.costBasis || 0) * (t.quantity || 0);
        } else if (t.type === 'CSP') {
          groups[t.ticker].totalExposure += (t.strike || 0) * (t.contracts || 1) * 100;
        }
      }
    });
    
    // Sort trades within each group: options by DTE ascending, shares at bottom
    Object.values(groups).forEach(group => {
      group.trades.sort((a, b) => {
        // Shares go to bottom
        if (a.tradeType === 'shares' && b.tradeType !== 'shares') return 1;
        if (a.tradeType !== 'shares' && b.tradeType === 'shares') return -1;
        if (a.tradeType === 'shares' && b.tradeType === 'shares') return 0;
        
        // Options sorted by DTE (ascending - most urgent first)
        const dteA = getDTE(a.expiration) ?? 999;
        const dteB = getDTE(b.expiration) ?? 999;
        return dteA - dteB;
      });
    });
    
    // Sort groups by total exposure (highest first)
    return Object.values(groups).sort((a, b) => b.totalExposure - a.totalExposure);
  }, [filteredTrades]);

  const stats = useMemo(() => {
    // Premium-based: include ALL option trades (open or closed)
    const options = trades.filter(t => t.tradeType === 'option');
    const soldShares = trades.filter(t => t.tradeType === 'shares' && t.status === 'sold');
    
    // Total P/L = all option premiums + sold shares
    const optionPL = options.reduce((s, t) => s + (calcPL(t) || 0), 0);
    const sharesPL = soldShares.reduce((s, t) => s + (calcPL(t) || 0), 0);
    const totalPL = optionPL + sharesPL;
    
    // Wins/losses based on final P/L per trade
    const allWithPL = [...options, ...soldShares];
    const wins = allWithPL.filter(t => (calcPL(t) || 0) > 0).length;
    const losses = allWithPL.filter(t => (calcPL(t) || 0) < 0).length;
    
    // Open positions
    const openOpts = trades.filter(t => t.status === 'open' && t.tradeType === 'option');
    const pendingPremium = openOpts.reduce((s, t) => s + (t.premium || 0) * (t.contracts || 1) * 100, 0);
    
    // Today's P/L - options opened today + shares sold today
    const todayOptions = options.filter(t => t.openDate === today);
    const todayShares = soldShares.filter(t => t.closeDate === today);
    const todayPL = todayOptions.reduce((s, t) => s + (calcPL(t) || 0), 0) + todayShares.reduce((s, t) => s + (calcPL(t) || 0), 0);
    
    // Week P/L
    const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
    const weekStartStr = weekStart.toISOString().split('T')[0];
    const weekOptions = options.filter(t => t.openDate >= weekStartStr);
    const weekShares = soldShares.filter(t => t.closeDate >= weekStartStr);
    const weekPL = weekOptions.reduce((s, t) => s + (calcPL(t) || 0), 0) + weekShares.reduce((s, t) => s + (calcPL(t) || 0), 0);
    
    // Month P/L
    const monthStart = new Date(); monthStart.setDate(1);
    const monthStartStr = monthStart.toISOString().split('T')[0];
    const monthOptions = options.filter(t => t.openDate >= monthStartStr);
    const monthShares = soldShares.filter(t => t.closeDate >= monthStartStr);
    const monthPL = monthOptions.reduce((s, t) => s + (calcPL(t) || 0), 0) + monthShares.reduce((s, t) => s + (calcPL(t) || 0), 0);
    
    // Enhanced stats
    const pls = allWithPL.map(t => calcPL(t) || 0);
    const largestWin = pls.length > 0 ? Math.max(...pls, 0) : 0;
    const largestLoss = pls.length > 0 ? Math.min(...pls, 0) : 0;
    const tradingDays = [...new Set(options.map(t => t.openDate).filter(Boolean))].length;
    const avgDailyPL = tradingDays > 0 ? optionPL / tradingDays : 0;
    const totalFees = allWithPL.reduce((s, t) => s + (t.entryFees || 0) + (t.exitFees || 0), 0);
    
    return { totalPL, wins, losses, winRate: allWithPL.length ? (wins / allWithPL.length * 100).toFixed(0) : 0, openOpts: openOpts.length, pendingPremium, todayPL, weekPL, monthPL, totalTrades: allWithPL.length, largestWin, largestLoss, avgDailyPL, tradingDays, totalFees };
  }, [trades]);

  // Daily P/L Log - Premium-based (options by openDate, shares by closeDate)
  const dailyPLLog = useMemo(() => {
    const byDate = {};
    
    // Options: attribute P/L to OPEN date
    trades.filter(t => t.tradeType === 'option').forEach(t => {
      const date = t.openDate;
      if (!date) return;
      if (!byDate[date]) byDate[date] = { date, trades: 0, grossPL: 0, fees: 0, netPL: 0 };
      
      const mult = (t.contracts || 1) * 100;
      const grossPremium = (t.premium || 0) * mult;
      const exitCost = (t.exitPremium || 0) * mult;
      const fees = (t.entryFees || 0) + (t.exitFees || 0);
      const netPL = grossPremium - exitCost - fees;
      
      byDate[date].trades++;
      byDate[date].grossPL += grossPremium;
      byDate[date].fees += fees + exitCost;
      byDate[date].netPL += netPL;
    });
    
    // Shares: attribute P/L to CLOSE date (when sold)
    trades.filter(t => t.tradeType === 'shares' && t.status === 'sold').forEach(t => {
      const date = t.closeDate;
      if (!date) return;
      if (!byDate[date]) byDate[date] = { date, trades: 0, grossPL: 0, fees: 0, netPL: 0 };
      
      const pl = calcPL(t) || 0;
      const fees = (t.entryFees || 0) + (t.exitFees || 0);
      
      byDate[date].trades++;
      byDate[date].grossPL += pl + fees;
      byDate[date].fees += fees;
      byDate[date].netPL += pl;
    });
    
    return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
  }, [trades]);

  // Weekly Breakdown Table - Premium-based (options by openDate)
  const weeklyBreakdown = useMemo(() => {
    const byWeek = {};
    
    // Helper to add P/L to the correct week/day
    const addToWeek = (dateStr, pl) => {
      const date = new Date(dateStr + 'T12:00:00');
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date);
      monday.setDate(diff);
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!byWeek[weekKey]) {
        byWeek[weekKey] = { weekStart: weekKey, mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, total: 0, trades: 0 };
      }
      
      const dayOfWeek = new Date(dateStr + 'T12:00:00').getDay();
      
      if (dayOfWeek === 1) byWeek[weekKey].mon += pl;
      else if (dayOfWeek === 2) byWeek[weekKey].tue += pl;
      else if (dayOfWeek === 3) byWeek[weekKey].wed += pl;
      else if (dayOfWeek === 4) byWeek[weekKey].thu += pl;
      else if (dayOfWeek === 5) byWeek[weekKey].fri += pl;
      // Weekend trades go to total only
      
      byWeek[weekKey].total += pl;
      byWeek[weekKey].trades++;
    };
    
    // Options: use OPEN date
    trades.filter(t => t.tradeType === 'option' && t.openDate).forEach(t => {
      addToWeek(t.openDate, calcPL(t) || 0);
    });
    
    // Shares: use CLOSE date (when sold)
    trades.filter(t => t.tradeType === 'shares' && t.status === 'sold' && t.closeDate).forEach(t => {
      addToWeek(t.closeDate, calcPL(t) || 0);
    });
    
    return Object.values(byWeek).sort((a, b) => b.weekStart.localeCompare(a.weekStart)).slice(0, 20);
  }, [trades]);

  const analytics = useMemo(() => {
    // Premium-based: include ALL options (open and closed)
    const allOptions = trades.filter(t => t.tradeType === 'option');
    const closedOptions = allOptions.filter(t => t.status !== 'open');
    
    const tradesWithDTE = closedOptions.filter(t => t.openDate && t.closeDate);
    const avgHoldTime = tradesWithDTE.length > 0 ? tradesWithDTE.reduce((sum, t) => { const open = new Date(t.openDate); const close = new Date(t.closeDate); return sum + Math.ceil((close - open) / (1000 * 60 * 60 * 24)); }, 0) / tradesWithDTE.length : 0;
    const sortedByDate = [...allOptions].sort((a, b) => (a.openDate || '').localeCompare(b.openDate || ''));
    let maxWinStreak = 0, maxLossStreak = 0, tempStreak = 0, lastWasWin = null, currentStreak = 0;
    sortedByDate.forEach(t => { const isWin = (calcPL(t) || 0) > 0; if (lastWasWin === null) { tempStreak = 1; lastWasWin = isWin; } else if (isWin === lastWasWin) { tempStreak++; } else { if (lastWasWin) maxWinStreak = Math.max(maxWinStreak, tempStreak); else maxLossStreak = Math.max(maxLossStreak, tempStreak); tempStreak = 1; lastWasWin = isWin; } });
    if (lastWasWin) maxWinStreak = Math.max(maxWinStreak, tempStreak); else maxLossStreak = Math.max(maxLossStreak, tempStreak);
    for (let i = sortedByDate.length - 1; i >= 0; i--) { const isWin = (calcPL(sortedByDate[i]) || 0) > 0; if (i === sortedByDate.length - 1) { currentStreak = isWin ? 1 : -1; } else { const prevWin = currentStreak > 0; if (isWin === prevWin) currentStreak += isWin ? 1 : -1; else break; } }
    
    // byDayOfWeek - Premium-based: use OPEN date
    const byDayOfWeek = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }; const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    allOptions.forEach(t => { if (t.openDate) { const day = new Date(t.openDate + 'T12:00:00').getDay(); byDayOfWeek[day] += calcPL(t) || 0; } });
    
    const bestDay = Object.entries(byDayOfWeek).reduce((best, [day, pl]) => pl > best.pl ? { day: parseInt(day), pl } : best, { day: 0, pl: -Infinity });
    const byTicker = {}; allOptions.forEach(t => { if (!byTicker[t.ticker]) byTicker[t.ticker] = 0; byTicker[t.ticker] += calcPL(t) || 0; });
    const tickerData = Object.entries(byTicker).map(([ticker, value]) => ({ name: ticker, value: Math.round(value * 100) / 100 })).filter(d => d.value > 0).sort((a, b) => b.value - a.value).slice(0, 6);
    const dayData = Object.entries(byDayOfWeek).map(([day, pl]) => ({ name: dayNames[parseInt(day)], pl: Math.round(pl * 100) / 100 }));
    return { avgHoldTime: Math.round(avgHoldTime * 10) / 10, currentStreak, maxWinStreak, maxLossStreak, bestDay: dayNames[bestDay.day], bestDayPL: bestDay.pl, tickerData, dayData };
  }, [trades]);

  const calendarData = useMemo(() => {
    const year = calendarMonth.getFullYear(); const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1); const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay(); const totalDays = lastDay.getDate();
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let i = 1; i <= totalDays; i++) { const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`; const expiringTrades = trades.filter(t => t.expiration === dateStr && t.status === 'open'); days.push({ day: i, date: dateStr, trades: expiringTrades }); }
    return { days, monthName: firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
  }, [trades, calendarMonth]);

  const getChartData = useMemo(() => {
    // Premium-based: options use openDate, shares use closeDate
    const options = trades.filter(t => t.tradeType === 'option' && t.openDate);
    const soldShares = trades.filter(t => t.tradeType === 'shares' && t.status === 'sold' && t.closeDate);
    
    if (options.length === 0 && soldShares.length === 0) return [];
    
    const now = new Date(); let startDate;
    switch (chartPeriod) { case '1W': startDate = new Date(now); startDate.setDate(now.getDate() - 7); break; case '1M': startDate = new Date(now); startDate.setMonth(now.getMonth() - 1); break; case '3M': startDate = new Date(now); startDate.setMonth(now.getMonth() - 3); break; case 'YTD': startDate = new Date(now.getFullYear(), 0, 1); break; case '1Y': startDate = new Date(now); startDate.setFullYear(now.getFullYear() - 1); break; default: startDate = new Date('2000-01-01'); }
    const startStr = startDate.toISOString().split('T')[0];
    
    // Build P/L by date
    const byDate = {};
    
    // Options: by openDate
    options.forEach(t => {
      const date = t.openDate;
      if (!byDate[date]) byDate[date] = 0;
      byDate[date] += calcPL(t) || 0;
    });
    
    // Shares: by closeDate
    soldShares.forEach(t => {
      const date = t.closeDate;
      if (!byDate[date]) byDate[date] = 0;
      byDate[date] += calcPL(t) || 0;
    });
    
    // Filter to period and calculate cumulative
    const allDates = Object.keys(byDate).sort();
    const beforePeriod = allDates.filter(d => d < startStr);
    let cumBefore = beforePeriod.reduce((sum, d) => sum + byDate[d], 0);
    
    const inPeriod = allDates.filter(d => d >= startStr);
    let cum = cumBefore;
    return inPeriod.map(d => { cum += byDate[d]; return { date: d, displayDate: d.slice(5), pl: Math.round(byDate[d] * 100) / 100, cumulative: Math.round(cum * 100) / 100 }; });
  }, [trades, chartPeriod]);

  const periodPL = useMemo(() => getChartData.reduce((sum, d) => sum + d.pl, 0), [getChartData]);

  const weekData = useMemo(() => {
    const getWeekStart = (offset) => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1 + offset * 7); return d; };
    const monday = getWeekStart(selectedWeek);
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    
    const data = days.map((name, i) => { 
      const date = new Date(monday); 
      date.setDate(monday.getDate() + i); 
      const dateStr = date.toISOString().split('T')[0]; 
      
      // Premium-based: options by openDate, shares by closeDate
      const dayOptions = trades.filter(t => t.tradeType === 'option' && t.openDate === dateStr);
      const dayShares = trades.filter(t => t.tradeType === 'shares' && t.status === 'sold' && t.closeDate === dateStr);
      
      const pl = dayOptions.reduce((s, t) => s + (calcPL(t) || 0), 0) + dayShares.reduce((s, t) => s + (calcPL(t) || 0), 0);
      const tradeCount = dayOptions.length + dayShares.length;
      
      return { name, date: dateStr, pl, trades: tradeCount }; 
    });
    
    const total = data.reduce((s, d) => s + d.pl, 0);
    const friday = new Date(monday); friday.setDate(monday.getDate() + 4);
    return { days: data, total, start: monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), end: friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), year: monday.getFullYear() };
  }, [trades, selectedWeek]);

  const fmtMoney = (v, showSign = false) => {
    const sign = showSign && v > 0 ? '+' : '';
    return sign + '$' + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload[0].payload;
      return (
        <div style={styles.tooltip}>
          <div style={styles.tooltipDate}>{d.date}</div>
          <div style={{ ...styles.tooltipValue, color: d.cumulative >= 0 ? colors.profit : colors.loss }}>{fmtMoney(d.cumulative, true)}</div>
          <div style={styles.tooltipChange}>Day: {fmtMoney(d.pl, true)}</div>
        </div>
      );
    }
    return null;
  };

  const PIE_COLORS = [colors.accent, colors.profit, colors.purple, colors.warning, colors.info, '#ec4899'];

  // Calendar View
  if (showModal === 'calendar') {
    return (
      <div style={styles.container}>
        <div style={styles.pageHeader}>
          <button style={styles.backButton} onClick={() => setShowModal(null)}>
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
          <h1 style={styles.pageTitle}>Expiration Calendar</h1>
          <div style={{ width: 80 }} />
        </div>
        
        <div style={styles.calendarHeader}>
          <button style={styles.navButton} onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}>
            <ChevronLeft size={18} />
          </button>
          <span style={styles.monthLabel}>{calendarData.monthName}</span>
          <button style={styles.navButton} onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}>
            <ChevronRight size={18} />
          </button>
        </div>
        
        <div style={styles.calendarGrid}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
            <div key={i} style={styles.calendarDayHeader}>{d}</div>
          ))}
          {calendarData.days.map((day, i) => (
            <div key={i} style={{ ...styles.calendarCell, ...(day?.date === today ? styles.calendarToday : {}) }}>
              {day && (
                <>
                  <span style={styles.calendarDayNum}>{day.day}</span>
                  {day.trades.slice(0, 2).map(t => {
                    const dte = getDTE(t.expiration);
                    const config = dte <= 3 ? { bg: colors.lossMuted, color: colors.loss } : dte <= 7 ? { bg: colors.warningMuted, color: colors.warning } : { bg: colors.profitMuted, color: colors.profit };
                    return <div key={t.id} style={{ ...styles.calendarTrade, background: config.bg, color: config.color }}>{t.ticker}</div>;
                  })}
                  {day.trades.length > 2 && <div style={styles.calendarMore}>+{day.trades.length - 2}</div>}
                </>
              )}
            </div>
          ))}
        </div>
        
        <div style={styles.legendRow}>
          <div style={styles.legendItem}><span style={{ ...styles.legendDot, background: colors.loss }} /> ≤3 DTE</div>
          <div style={styles.legendItem}><span style={{ ...styles.legendDot, background: colors.warning }} /> 4-7 DTE</div>
          <div style={styles.legendItem}><span style={{ ...styles.legendDot, background: colors.profit }} /> 7+ DTE</div>
        </div>
      </div>
    );
  }

  // Analytics View
  if (showModal === 'analytics') {
    return (
      <div style={styles.container}>
        <div style={styles.pageHeader}>
          <button style={styles.backButton} onClick={() => setShowModal(null)}>
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
          <h1 style={styles.pageTitle}>Analytics</h1>
          <div style={{ width: 80 }} />
        </div>

        <div style={styles.analyticsGrid}>
          <div style={styles.metricCard}>
            <div style={styles.metricIcon}><Clock size={20} /></div>
            <div style={styles.metricContent}>
              <span style={styles.metricLabel}>Avg Hold Time</span>
              <span style={styles.metricValue}>{analytics.avgHoldTime} days</span>
            </div>
          </div>
          <div style={styles.metricCard}>
            <div style={{ ...styles.metricIcon, background: analytics.currentStreak > 0 ? colors.profitMuted : colors.lossMuted }}>
              <Activity size={20} style={{ color: analytics.currentStreak > 0 ? colors.profit : colors.loss }} />
            </div>
            <div style={styles.metricContent}>
              <span style={styles.metricLabel}>Current Streak</span>
              <span style={{ ...styles.metricValue, color: analytics.currentStreak > 0 ? colors.profit : analytics.currentStreak < 0 ? colors.loss : colors.textSecondary }}>
                {analytics.currentStreak > 0 ? `${analytics.currentStreak}W` : analytics.currentStreak < 0 ? `${Math.abs(analytics.currentStreak)}L` : '-'}
              </span>
            </div>
          </div>
          <div style={styles.metricCard}>
            <div style={{ ...styles.metricIcon, background: colors.profitMuted }}>
              <TrendingUp size={20} style={{ color: colors.profit }} />
            </div>
            <div style={styles.metricContent}>
              <span style={styles.metricLabel}>Best Streak</span>
              <span style={{ ...styles.metricValue, color: colors.profit }}>{analytics.maxWinStreak}W</span>
            </div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricIcon}><CalendarDays size={20} /></div>
            <div style={styles.metricContent}>
              <span style={styles.metricLabel}>Best Day</span>
              <span style={styles.metricValue}>{analytics.bestDay}</span>
            </div>
          </div>
        </div>

        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>P/L by Ticker</h2>
        </div>
        {analytics.tickerData.length > 0 ? (
          <div style={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={analytics.tickerData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} strokeWidth={0}>
                  {analytics.tickerData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={styles.tooltip} formatter={v => fmtMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div style={styles.pieLegend}>
              {analytics.tickerData.map((t, i) => (
                <div key={t.name} style={styles.pieLegendItem}>
                  <span style={{ ...styles.legendDot, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span style={styles.pieLegendTicker}>{t.name}</span>
                  <span style={{ ...styles.pieLegendValue, color: colors.profit }}>{fmtMoney(t.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div style={styles.emptyText}>No closed trades yet</div>}

        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>P/L by Day of Week</h2>
        </div>
        <div style={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.dayData} margin={{ top: 20, right: 20, left: -10, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: colors.textTertiary, fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: colors.textTertiary, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip contentStyle={styles.tooltip} formatter={v => fmtMoney(v)} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Bar dataKey="pl" radius={[4, 4, 0, 0]}>
                {analytics.dayData.map((entry, i) => <Cell key={i} fill={entry.pl >= 0 ? colors.profit : colors.loss} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Dashboard View
  if (showModal === 'dashboard') {
    const chartColor = periodPL >= 0 ? colors.profit : colors.loss;
    const totalValue = getChartData.length > 0 ? getChartData[getChartData.length - 1].cumulative : stats.totalPL;
    return (
      <div style={styles.container}>
        <div style={styles.pageHeader}>
          <button style={styles.backButton} onClick={() => setShowModal(null)}>
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
          <h1 style={styles.pageTitle}>Performance Dashboard</h1>
          <div style={{ width: 80 }} />
        </div>

        {/* Dashboard Tabs */}
        <div style={styles.dashboardTabs}>
          <button style={{ ...styles.dashboardTab, ...(dashboardTab === 'overview' ? styles.dashboardTabActive : {}) }} onClick={() => setDashboardTab('overview')}>Overview</button>
          <button style={{ ...styles.dashboardTab, ...(dashboardTab === 'daily' ? styles.dashboardTabActive : {}) }} onClick={() => setDashboardTab('daily')}>Daily Log</button>
          <button style={{ ...styles.dashboardTab, ...(dashboardTab === 'weekly' ? styles.dashboardTabActive : {}) }} onClick={() => setDashboardTab('weekly')}>Weekly</button>
        </div>

        {/* OVERVIEW TAB */}
        {dashboardTab === 'overview' && (
          <>
            {/* Enhanced Stats Row */}
            <div style={styles.enhancedStatsGrid}>
              <div style={styles.enhancedStatCard}>
                <div style={styles.enhancedStatLabel}>Total Net P&L</div>
                <div style={{ ...styles.enhancedStatValue, color: stats.totalPL >= 0 ? colors.profit : colors.loss }}>{fmtMoney(stats.totalPL, true)}</div>
              </div>
              <div style={styles.enhancedStatCard}>
                <div style={styles.enhancedStatLabel}>Avg Daily P&L</div>
                <div style={{ ...styles.enhancedStatValue, color: stats.avgDailyPL >= 0 ? colors.profit : colors.loss }}>{fmtMoney(stats.avgDailyPL, true)}</div>
              </div>
              <div style={styles.enhancedStatCard}>
                <div style={styles.enhancedStatLabel}>Win Rate</div>
                <div style={styles.enhancedStatValue}>{stats.winRate}%</div>
              </div>
              <div style={styles.enhancedStatCard}>
                <div style={styles.enhancedStatLabel}>Largest Win</div>
                <div style={{ ...styles.enhancedStatValue, color: colors.profit }}>{fmtMoney(stats.largestWin)}</div>
              </div>
              <div style={styles.enhancedStatCard}>
                <div style={styles.enhancedStatLabel}>Largest Loss</div>
                <div style={{ ...styles.enhancedStatValue, color: colors.loss }}>{fmtMoney(stats.largestLoss)}</div>
              </div>
            </div>

            {/* Period Selector */}
            <div style={styles.periodSelector}>
              {['1W', '1M', '3M', 'YTD', '1Y', 'ALL'].map(p => (
                <button key={p} style={{ ...styles.periodButton, ...(chartPeriod === p ? styles.periodButtonActive : {}) }} onClick={() => setChartPeriod(p)}>{p}</button>
              ))}
            </div>

            {/* Cumulative P/L Chart */}
            {getChartData.length > 1 && (
              <div style={styles.chartContainer}>
                <div style={styles.chartHeader}>
                  <span style={styles.chartTitle}>Cumulative P/L</span>
                  <span style={{ ...styles.chartValue, color: totalValue >= 0 ? colors.profit : colors.loss }}>{fmtMoney(totalValue, true)}</span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={getChartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColor} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="displayDate" tick={{ fill: colors.textTertiary, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: colors.textTertiary, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="cumulative" stroke={chartColor} fill="url(#chartGradient)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Quick Stats Row */}
            <div style={styles.quickStatsRow}>
              <div style={styles.quickStat}>
                <span style={styles.quickStatLabel}>Trading Days</span>
                <span style={styles.quickStatValue}>{stats.tradingDays}</span>
              </div>
              <div style={styles.quickStat}>
                <span style={styles.quickStatLabel}>Total Trades</span>
                <span style={styles.quickStatValue}>{stats.totalTrades}</span>
              </div>
              <div style={styles.quickStat}>
                <span style={styles.quickStatLabel}>Total Fees</span>
                <span style={styles.quickStatValue}>{fmtMoney(stats.totalFees)}</span>
              </div>
              <div style={styles.quickStat}>
                <span style={styles.quickStatLabel}>W/L</span>
                <span style={styles.quickStatValue}>{stats.wins}/{stats.losses}</span>
              </div>
            </div>
          </>
        )}

        {/* DAILY LOG TAB */}
        {dashboardTab === 'daily' && (
          <div style={styles.dailyLogContainer}>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}># Trades</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Gross P/L</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Fees</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Net P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyPLLog.map(day => (
                    <tr key={day.date} style={styles.tr}>
                      <td style={styles.td}>{day.date}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{day.trades}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: day.grossPL >= 0 ? colors.profit : colors.loss, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(day.grossPL, true)}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(day.fees)}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: day.netPL >= 0 ? colors.profit : colors.loss, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(day.netPL, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {dailyPLLog.length === 0 && (
              <div style={styles.emptyState}>
                <p>No closed trades yet</p>
              </div>
            )}
          </div>
        )}

        {/* WEEKLY BREAKDOWN TAB */}
        {dashboardTab === 'weekly' && (
          <div style={styles.weeklyContainer}>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Week Start</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Mon</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Tue</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Wed</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Thu</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Fri</th>
                    <th style={{ ...styles.th, textAlign: 'right', background: colors.bgTertiary }}>Weekly P/L</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyBreakdown.map(week => (
                    <tr key={week.weekStart} style={styles.tr}>
                      <td style={{ ...styles.td, fontWeight: 500 }}>{week.weekStart}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.mon > 0 ? colors.profit : week.mon < 0 ? colors.loss : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{week.mon !== 0 ? fmtMoney(week.mon, true) : '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.tue > 0 ? colors.profit : week.tue < 0 ? colors.loss : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{week.tue !== 0 ? fmtMoney(week.tue, true) : '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.wed > 0 ? colors.profit : week.wed < 0 ? colors.loss : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{week.wed !== 0 ? fmtMoney(week.wed, true) : '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.thu > 0 ? colors.profit : week.thu < 0 ? colors.loss : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{week.thu !== 0 ? fmtMoney(week.thu, true) : '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.fri > 0 ? colors.profit : week.fri < 0 ? colors.loss : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{week.fri !== 0 ? fmtMoney(week.fri, true) : '—'}</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: week.total >= 0 ? colors.profit : colors.loss, fontWeight: 600, background: colors.bgTertiary, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(week.total, true)}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{week.trades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {weeklyBreakdown.length === 0 && (
              <div style={styles.emptyState}>
                <p>No closed trades yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Roll History Modal
  if (showModal === 'rollHistory' && modalData) {
    const chain = getRollChain(modalData);
    const totalPremium = getRollChainPremium(modalData);
    return (
      <div style={styles.modalOverlay} onClick={() => setShowModal(null)}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Roll History</h2>
            <button style={styles.modalCloseBtn} onClick={() => setShowModal(null)}><X size={20} /></button>
          </div>
          <div style={styles.rollSummary}>
            <span style={styles.rollSummaryLabel}>Total Premium Collected</span>
            <span style={{ ...styles.rollSummaryValue, color: totalPremium >= 0 ? colors.profit : colors.loss }}>{fmtMoney(totalPremium, true)}</span>
          </div>
          <div style={styles.rollTimeline}>
            {chain.map((t, i) => {
              const pl = calcPL(t);
              const config = statusConfig[t.status];
              return (
                <div key={t.id} style={styles.rollItem}>
                  <div style={styles.rollDot} />
                  {i < chain.length - 1 && <div style={styles.rollLine} />}
                  <div style={styles.rollContent}>
                    <div style={styles.rollHeader}>
                      <span style={styles.rollType}>{t.type} ${t.strike}</span>
                      <span style={{ ...styles.statusBadge, background: config.bg, color: config.color }}>{config.label}</span>
                    </div>
                    <div style={styles.rollMeta}>
                      <span>Premium: ${t.premium?.toFixed(2)}</span>
                      <span>{t.openDate}</span>
                    </div>
                    {pl !== null && <div style={{ ...styles.rollPL, color: pl >= 0 ? colors.profit : colors.loss }}>{fmtMoney(pl, true)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (authLoading) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <div style={styles.loginLogoIcon}>
            <Layers size={40} />
          </div>
          <h1 style={styles.loginTitle}>WheelStrat</h1>
          <p style={styles.loginSubtitle}>Loading...</p>
        </div>
      </div>
    );
  }

  // Landing Page (shown when not authenticated and Firebase is enabled)
  if (firebaseEnabled && !user) {
    return <LandingPage onGetStarted={handleSignIn} />;
  }

  // Main View
  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <Layers size={24} />
            </div>
            <div style={styles.logoText}>
              <span style={styles.logoTitle}>WheelStrat</span>
              <span style={styles.logoSubtitle}>Premium Collection</span>
            </div>
          </div>
        </div>
        <div style={styles.headerRight}>
          {/* Cloud Status Indicator */}
          <div style={{
            ...styles.cloudStatus,
            background: cloudStatus === 'synced' ? colors.profitMuted : 
                       cloudStatus === 'syncing' ? colors.warningMuted : 
                       cloudStatus === 'error' ? colors.lossMuted : colors.bgTertiary,
            color: cloudStatus === 'synced' ? colors.profit : 
                   cloudStatus === 'syncing' ? colors.warning : 
                   cloudStatus === 'error' ? colors.loss : colors.textTertiary
          }} title={
            cloudStatus === 'synced' ? 'Synced to cloud' :
            cloudStatus === 'syncing' ? 'Syncing...' :
            cloudStatus === 'error' ? 'Sync error - using local storage' :
            cloudStatus === 'connecting' ? 'Connecting...' : 'Cloud sync disabled'
          }>
            {cloudStatus === 'disabled' || cloudStatus === 'error' ? <CloudOff size={16} /> : <Cloud size={16} />}
            <span style={styles.cloudStatusText}>
              {cloudStatus === 'synced' ? 'Synced' :
               cloudStatus === 'syncing' ? 'Syncing' :
               cloudStatus === 'connecting' ? 'Connecting' :
               cloudStatus === 'error' ? 'Offline' : 'Local'}
            </span>
          </div>
          <button style={styles.headerButton} onClick={exportTrades} title="Download Backup">
            <Download size={18} />
          </button>
          <button style={styles.headerButton} onClick={() => setShowModal('calendar')}>
            <CalendarDays size={18} />
          </button>
          <button style={styles.headerButton} onClick={() => setShowModal('analytics')}>
            <Activity size={18} />
          </button>
          <button style={styles.headerButton} onClick={() => setShowModal('dashboard')}>
            <BarChart3 size={18} />
          </button>
          <button style={styles.secondaryHeaderButton} onClick={() => setShowModal('bulk')}>
            <Layers size={18} />
            <span>Bulk Add</span>
          </button>
          <button style={styles.secondaryHeaderButton} onClick={() => importFileRef.current?.click()}>
            <Upload size={18} />
            <span>Import</span>
          </button>
          <button style={styles.primaryButton} onClick={() => setShowModal('trade')}>
            <Plus size={18} />
            <span>New Trade</span>
          </button>
          
          {/* User Menu */}
          {user && (
            <div style={styles.userMenu}>
              {user.photoURL ? (
                <img src={user.photoURL} alt="" style={styles.userAvatar} />
              ) : (
                <div style={styles.userAvatarPlaceholder}>
                  <User size={16} />
                </div>
              )}
              <button style={styles.signOutButton} onClick={handleSignOut} title="Sign Out">
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <span style={styles.statLabel}>Total P/L</span>
            <div style={{ ...styles.statIcon, background: stats.totalPL >= 0 ? colors.profitMuted : colors.lossMuted }}>
              <DollarSign size={16} style={{ color: stats.totalPL >= 0 ? colors.profit : colors.loss }} />
            </div>
          </div>
          <div style={{ ...styles.statValue, color: stats.totalPL >= 0 ? colors.profit : colors.loss }}>
            {fmtMoney(stats.totalPL, true)}
          </div>
          <div style={styles.statFooter}>
            <span style={{ color: stats.weekPL >= 0 ? colors.profit : colors.loss, display: 'flex', alignItems: 'center', gap: 4 }}>
              {stats.weekPL >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {fmtMoney(Math.abs(stats.weekPL))}
            </span>
            <span>this week</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <span style={styles.statLabel}>Pending Premium</span>
            <div style={styles.statIcon}>
              <Clock size={16} />
            </div>
          </div>
          <div style={styles.statValue}>{fmtMoney(stats.pendingPremium)}</div>
          <div style={styles.statFooter}>
            <span style={{ color: colors.accent }}>{stats.openOpts} positions</span>
            <span>open</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <span style={styles.statLabel}>Win Rate</span>
            <div style={{ ...styles.statIcon, background: colors.profitMuted }}>
              <Target size={16} style={{ color: colors.profit }} />
            </div>
          </div>
          <div style={styles.statValue}>{stats.winRate}%</div>
          <div style={styles.statFooter}>
            <span style={{ color: colors.profit }}>{stats.wins}W</span>
            <span>/</span>
            <span style={{ color: colors.loss }}>{stats.losses}L</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <span style={styles.statLabel}>This Month</span>
            <div style={{ ...styles.statIcon, background: stats.monthPL >= 0 ? colors.profitMuted : colors.lossMuted }}>
              <TrendingUp size={16} style={{ color: stats.monthPL >= 0 ? colors.profit : colors.loss }} />
            </div>
          </div>
          <div style={{ ...styles.statValue, color: stats.monthPL >= 0 ? colors.profit : colors.loss }}>
            {fmtMoney(stats.monthPL, true)}
          </div>
          <div style={styles.statFooter}>
            <span>{stats.totalTrades} trades</span>
            <span>closed</span>
          </div>
        </div>
      </div>

      {/* Quick Entry */}
      {showQuickEntry ? (
        <form onSubmit={handleQuickEntry} style={styles.quickEntryCard}>
          <div style={styles.quickEntryHeader}>
            <Zap size={16} style={{ color: colors.accent }} />
            <span style={styles.quickEntryTitle}>Quick Entry</span>
          </div>
          <div style={styles.quickFavorites}>
            {favorites.map(f => (
              <button key={f} type="button" style={{ ...styles.favoriteChip, ...(quickForm.ticker === f ? styles.favoriteChipActive : {}) }} onClick={() => setQuickForm({ ...quickForm, ticker: f })}>{f}</button>
            ))}
          </div>
          <div style={styles.quickEntryRow}>
            <input ref={quickEntryRef} style={styles.quickInput} placeholder="TICKER" value={quickForm.ticker} onChange={e => setQuickForm({ ...quickForm, ticker: e.target.value.toUpperCase() })} />
            <select style={styles.quickSelect} value={quickForm.type} onChange={e => setQuickForm({ ...quickForm, type: e.target.value })}>
              <option value="CSP">CSP</option>
              <option value="CC">CC</option>
            </select>
            <input style={styles.quickInput} placeholder="Strike" type="number" step="0.01" value={quickForm.strike} onChange={e => setQuickForm({ ...quickForm, strike: e.target.value })} />
            <input style={styles.quickInput} placeholder="Premium" type="number" step="0.01" value={quickForm.premium} onChange={e => setQuickForm({ ...quickForm, premium: e.target.value })} />
            <input style={{ ...styles.quickInput, width: 60 }} placeholder="Qty" type="number" min="1" value={quickForm.contracts} onChange={e => setQuickForm({ ...quickForm, contracts: e.target.value })} />
            <button type="submit" style={styles.quickSubmitBtn}><Check size={18} /></button>
            <button type="button" style={styles.quickCancelBtn} onClick={() => setShowQuickEntry(false)}><X size={18} /></button>
          </div>
        </form>
      ) : (
        <button style={styles.quickEntryButton} onClick={() => { setShowQuickEntry(true); setTimeout(() => quickEntryRef.current?.focus(), 100); }}>
          <Zap size={16} />
          <span>Quick Entry</span>
          <span style={styles.keyboardShortcut}>⌘N</span>
        </button>
      )}

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filterGroup}>
          {['all', 'open', 'closed', 'options', 'shares'].map(f => (
            <button key={f} style={{ ...styles.filterButton, ...(filter === f ? styles.filterButtonActive : {}) }} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div style={styles.toolbarRight}>
          {selectMode ? (
            <>
              <span style={styles.selectedCount}>{selectedTrades.size} selected</span>
              <button style={styles.toolbarButton} onClick={selectAllVisible}>Select All</button>
              <button style={{ ...styles.toolbarButton, color: colors.loss }} onClick={() => setConfirmDelete('bulk')}>Delete</button>
              <button style={styles.toolbarButton} onClick={() => { setSelectMode(false); setSelectedTrades(new Set()); }}>Cancel</button>
            </>
          ) : (
            <>
              <button style={styles.toolbarButton} onClick={() => setSelectMode(true)}>
                <CheckSquare size={16} />
              </button>
              <div style={styles.searchContainer}>
                <Search size={14} style={styles.searchIcon} />
                <input style={styles.searchInput} placeholder="Search ticker..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select style={styles.sortSelect} value={`${sortBy}-${sortDir}`} onChange={e => { const [b, d] = e.target.value.split('-'); setSortBy(b); setSortDir(d); }}>
                <option value="date-desc">Newest</option>
                <option value="date-asc">Oldest</option>
                <option value="pl-desc">P/L ↓</option>
                <option value="ticker-asc">A-Z</option>
              </select>
              <div style={styles.viewToggle}>
                <button style={{ ...styles.viewButton, ...(view === 'grouped' ? styles.viewButtonActive : {}) }} onClick={() => setView('grouped')} title="Grouped"><Layers size={16} /></button>
                <button style={{ ...styles.viewButton, ...(view === 'cards' ? styles.viewButtonActive : {}) }} onClick={() => setView('cards')} title="Cards"><Grid size={16} /></button>
                <button style={{ ...styles.viewButton, ...(view === 'table' ? styles.viewButtonActive : {}) }} onClick={() => setView('table')} title="Table"><List size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trade List */}
      {filteredTrades.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <Layers size={48} />
          </div>
          <h3 style={styles.emptyTitle}>No positions yet</h3>
          <p style={styles.emptyText}>Start tracking your wheel strategy trades</p>
        </div>
      ) : view === 'grouped' ? (
        <div style={styles.groupedContainer}>
          {groupedTrades.map(group => (
            <div key={group.ticker} style={styles.tickerGroup}>
              {/* Group Header */}
              <div style={styles.groupHeader}>
                <div style={styles.groupHeaderLeft}>
                  <span style={styles.groupTicker}>{group.ticker}</span>
                  <span style={styles.groupCount}>{group.positionCount} position{group.positionCount !== 1 ? 's' : ''}</span>
                </div>
                <div style={styles.groupHeaderRight}>
                  {group.totalPending > 0 && (
                    <div style={styles.groupStat}>
                      <span style={styles.groupStatLabel}>Pending</span>
                      <span style={{ ...styles.groupStatValue, color: colors.profit }}>+{fmtMoney(group.totalPending)}</span>
                    </div>
                  )}
                  {group.totalExposure > 0 && (
                    <div style={styles.groupStat}>
                      <span style={styles.groupStatLabel}>Exposure</span>
                      <span style={styles.groupStatValue}>{fmtMoney(group.totalExposure)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Group Trades */}
              <div style={styles.groupTrades}>
                {group.trades.map((t, idx) => {
                  const pl = calcPL(t);
                  const isShares = t.tradeType === 'shares';
                  const isOpen = t.status === 'open';
                  const dte = getDTE(t.expiration);
                  const config = statusConfig[t.status];
                  const isFirstShare = isShares && (idx === 0 || group.trades[idx - 1]?.tradeType !== 'shares');
                  
                  return (
                    <React.Fragment key={t.id}>
                      {isFirstShare && idx > 0 && <div style={styles.groupDivider} />}
                      <div style={styles.groupTradeRow}>
                        {/* Type Badge */}
                        <div style={styles.groupTradeType}>
                          <span style={{ 
                            ...styles.groupTypeBadge, 
                            background: isShares ? colors.profitMuted : t.type === 'CSP' ? colors.accentMuted : colors.purpleMuted, 
                            color: isShares ? colors.profit : t.type === 'CSP' ? colors.accent : colors.purple 
                          }}>
                            {isShares ? '📦' : t.type}
                          </span>
                        </div>
                        
                        {/* Strike/Cost */}
                        <div style={styles.groupTradeStrike}>
                          ${isShares ? t.costBasis?.toFixed(2) : t.strike?.toFixed(2)}
                        </div>
                        
                        {/* Premium */}
                        <div style={styles.groupTradePremium}>
                          {isShares ? '—' : `$${t.premium?.toFixed(2)}`}
                        </div>
                        
                        {/* Quantity */}
                        <div style={styles.groupTradeQty}>
                          x{isShares ? t.quantity : t.contracts}
                        </div>
                        
                        {/* Expiration */}
                        <div style={styles.groupTradeExp}>
                          {t.expiration ? t.expiration.slice(5) : '—'}
                        </div>
                        
                        {/* DTE */}
                        <div style={styles.groupTradeDTE}>
                          {isOpen && dte !== null ? (
                            <span style={{ 
                              ...styles.dteBadgeSmall, 
                              background: dte <= 3 ? colors.lossMuted : dte <= 7 ? colors.warningMuted : colors.profitMuted, 
                              color: dte <= 3 ? colors.loss : dte <= 7 ? colors.warning : colors.profit 
                            }}>
                              {dte}d
                            </span>
                          ) : '—'}
                        </div>
                        
                        {/* Status */}
                        <div style={styles.groupTradeStatus}>
                          <span style={{ ...styles.statusBadgeSmall, background: config.bg, color: config.color }}>{config.label}</span>
                        </div>
                        
                        {/* P/L */}
                        <div style={{ ...styles.groupTradePL, color: pl === null ? colors.textTertiary : pl >= 0 ? colors.profit : colors.loss }}>
                          {pl !== null ? fmtMoney(pl, true) : '—'}
                        </div>
                        
                        {/* Actions */}
                        <div style={styles.groupTradeActions}>
                          {isOpen && !isShares && (
                            <>
                              <button style={styles.groupActionBtn} onClick={() => { setModalData(t); setShowModal('close'); }} title="Close"><X size={14} /></button>
                              <button style={styles.groupActionBtn} onClick={() => { setShowRollModal(t); setRollForm({ strike: t.strike.toString(), premium: '', expiration: '', contracts: t.contracts }); }} title="Roll"><RefreshCw size={14} /></button>
                              <button style={{ ...styles.groupActionBtn, color: colors.profit }} onClick={() => handleExpire(t)} title="Expire"><Check size={14} /></button>
                            </>
                          )}
                          {isOpen && isShares && (
                            <button style={{ ...styles.groupActionBtn, color: colors.profit }} onClick={() => { setModalData({ ...t, isShares: true }); setShowModal('close'); }} title="Sell"><DollarSign size={14} /></button>
                          )}
                          <button style={styles.groupActionBtn} onClick={() => openEdit(t)} title="Edit"><Edit3 size={14} /></button>
                          <button style={{ ...styles.groupActionBtn, color: colors.loss }} onClick={() => setConfirmDelete(t.id)} title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : view === 'cards' ? (
        <div style={styles.cardsGrid}>
          {filteredTrades.map(t => {
            const pl = calcPL(t);
            const isShares = t.tradeType === 'shares';
            const isOpen = t.status === 'open';
            const isSelected = selectedTrades.has(t.id);
            const dte = getDTE(t.expiration);
            const hasRolls = t.rolledFrom || trades.some(tr => tr.rolledFrom === t.id);
            const config = statusConfig[t.status];
            
            return (
              <div key={t.id} style={{ ...styles.tradeCard, ...(isSelected ? styles.tradeCardSelected : {}) }}>
                <div style={styles.tradeCardHeader}>
                  <div style={styles.tradeCardLeft}>
                    {selectMode && (
                      <button style={styles.checkboxButton} onClick={() => toggleSelectTrade(t.id)}>
                        {isSelected ? <CheckSquare size={18} style={{ color: colors.accent }} /> : <Square size={18} />}
                      </button>
                    )}
                    <span style={styles.tradeTicker}>{t.ticker}</span>
                    <span style={{ ...styles.typeBadge, background: isShares ? colors.profitMuted : t.type === 'CSP' ? colors.accentMuted : colors.purpleMuted, color: isShares ? colors.profit : t.type === 'CSP' ? colors.accent : colors.purple }}>
                      {isShares ? `${t.quantity} shares` : t.type}
                    </span>
                  </div>
                  <div style={styles.tradeCardRight}>
                    {isOpen && dte !== null && (
                      <span style={{ ...styles.dteBadge, background: dte <= 3 ? colors.lossMuted : dte <= 7 ? colors.warningMuted : colors.profitMuted, color: dte <= 3 ? colors.loss : dte <= 7 ? colors.warning : colors.profit }}>
                        {dte}d
                      </span>
                    )}
                    {hasRolls && (
                      <button style={styles.rollIndicator} onClick={() => { setModalData(t); setShowModal('rollHistory'); }}>
                        <Link2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
                
                <div style={styles.tradeCardBody}>
                  {isShares ? (
                    <>
                      <div style={styles.tradeField}>
                        <span style={styles.tradeFieldLabel}>Cost Basis</span>
                        <span style={styles.tradeFieldValue}>${t.costBasis?.toFixed(2)}</span>
                      </div>
                      <div style={styles.tradeField}>
                        <span style={styles.tradeFieldLabel}>Total Value</span>
                        <span style={styles.tradeFieldValue}>${(t.costBasis * t.quantity).toLocaleString()}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.tradeField}>
                        <span style={styles.tradeFieldLabel}>Strike</span>
                        <span style={styles.tradeFieldValue}>${t.strike}</span>
                      </div>
                      <div style={styles.tradeField}>
                        <span style={styles.tradeFieldLabel}>Premium</span>
                        <span style={styles.tradeFieldValue}>${t.premium?.toFixed(2)}</span>
                      </div>
                      <div style={styles.tradeField}>
                        <span style={styles.tradeFieldLabel}>Contracts</span>
                        <span style={styles.tradeFieldValue}>{t.contracts}</span>
                      </div>
                    </>
                  )}
                  <div style={styles.tradeField}>
                    <span style={styles.tradeFieldLabel}>Opened</span>
                    <span style={styles.tradeFieldValue}>{t.openDate?.slice(5)}</span>
                  </div>
                  {t.expiration && (
                    <div style={styles.tradeField}>
                      <span style={styles.tradeFieldLabel}>Expires</span>
                      <span style={styles.tradeFieldValue}>{t.expiration?.slice(5)}</span>
                    </div>
                  )}
                </div>
                
                <div style={styles.tradeCardFooter}>
                  <span style={{ ...styles.statusBadge, background: config.bg, color: config.color }}>{config.label}</span>
                  {pl !== null && (
                    <span style={{ ...styles.tradePL, color: pl >= 0 ? colors.profit : colors.loss }}>{fmtMoney(pl, true)}</span>
                  )}
                </div>
                
                {!selectMode && (
                  <div style={styles.tradeCardActions}>
                    {isOpen && !isShares && (
                      <>
                        <button style={styles.actionButton} onClick={() => { setShowRollModal(t); setRollForm({ strike: t.strike.toString(), premium: '', expiration: '', contracts: t.contracts }); }} title="Roll">
                          <RefreshCw size={14} />
                        </button>
                        <button style={styles.actionButton} onClick={() => { setModalData(t); setShowModal('close'); }} title="Close">
                          <X size={14} />
                        </button>
                        <button style={{ ...styles.actionButton, color: colors.profit }} onClick={() => handleExpire(t)} title="Expire">
                          <Check size={14} />
                        </button>
                        <button style={{ ...styles.actionButton, color: colors.warning }} onClick={() => { setModalData(t); setCloseForm({ exitPremium: '', exitFees: '' }); setShowModal('assign'); }} title="Assign">
                          <Zap size={14} />
                        </button>
                      </>
                    )}
                    {isOpen && isShares && (
                      <button style={styles.actionButton} onClick={() => { setModalData({ ...t, isShares: true }); setShowModal('close'); }} title="Sell">
                        <DollarSign size={14} />
                      </button>
                    )}
                    <button style={styles.actionButton} onClick={() => openEdit(t)} title="Edit">
                      <Edit3 size={14} />
                    </button>
                    <button style={{ ...styles.actionButton, color: colors.loss }} onClick={() => setConfirmDelete(t.id)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {selectMode && <th style={styles.th}></th>}
                <th style={styles.th}>Symbol</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Strike</th>
                <th style={styles.th}>Premium</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Expiration</th>
                <th style={styles.th}>DTE</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>P/L</th>
                {!selectMode && <th style={styles.th}></th>}
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map(t => {
                const pl = calcPL(t);
                const isShares = t.tradeType === 'shares';
                const isOpen = t.status === 'open';
                const isSelected = selectedTrades.has(t.id);
                const dte = getDTE(t.expiration);
                const config = statusConfig[t.status];
                
                return (
                  <tr key={t.id} style={{ ...styles.tr, ...(isSelected ? styles.trSelected : {}) }}>
                    {selectMode && (
                      <td style={styles.td}>
                        <button style={styles.checkboxButton} onClick={() => toggleSelectTrade(t.id)}>
                          {isSelected ? <CheckSquare size={16} style={{ color: colors.accent }} /> : <Square size={16} />}
                        </button>
                      </td>
                    )}
                    <td style={{ ...styles.td, ...styles.tdTicker }}>{t.ticker}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.typeBadge, background: isShares ? colors.profitMuted : t.type === 'CSP' ? colors.accentMuted : colors.purpleMuted, color: isShares ? colors.profit : t.type === 'CSP' ? colors.accent : colors.purple }}>
                        {isShares ? 'Shares' : t.type}
                      </span>
                    </td>
                    <td style={styles.td}>${isShares ? t.costBasis?.toFixed(2) : t.strike?.toFixed(2)}</td>
                    <td style={styles.td}>{isShares ? '—' : '$' + t.premium?.toFixed(2)}</td>
                    <td style={styles.td}>{isShares ? t.quantity : t.contracts}</td>
                    <td style={styles.td}>{t.expiration?.slice(5) || '—'}</td>
                    <td style={{ ...styles.td, color: dte === null ? colors.textTertiary : dte <= 3 ? colors.loss : dte <= 7 ? colors.warning : colors.profit }}>{dte ?? '—'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, background: config.bg, color: config.color }}>{config.label}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: pl === null ? colors.textTertiary : pl >= 0 ? colors.profit : colors.loss, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {pl !== null ? fmtMoney(pl, true) : '—'}
                    </td>
                    {!selectMode && (
                      <td style={styles.td}>
                        <div style={styles.tableActions}>
                          {isOpen && !isShares && (
                            <button style={styles.actionButton} onClick={() => { setModalData(t); setShowModal('close'); }} title="Close"><X size={14} /></button>
                          )}
                          {isOpen && isShares && (
                            <button style={{ ...styles.actionButton, color: colors.profit }} onClick={() => { setModalData({ ...t, isShares: true }); setShowModal('close'); }} title="Sell"><DollarSign size={14} /></button>
                          )}
                          <button style={styles.actionButton} onClick={() => openEdit(t)} title="Edit"><Edit3 size={14} /></button>
                          <button style={{ ...styles.actionButton, color: colors.loss }} onClick={() => setConfirmDelete(t.id)} title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showModal && !['dashboard', 'calendar', 'analytics', 'rollHistory'].includes(showModal) && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={{ ...styles.modal, ...(showModal === 'bulk' ? { width: 560, maxWidth: 'calc(100vw - 40px)' } : {}) }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showModal === 'trade' ? (editingId ? 'Edit Trade' : 'New Trade') : 
                 showModal === 'shares' ? (editingId ? 'Edit Position' : 'Add Shares') : 
                 showModal === 'close' ? (modalData?.isShares ? 'Sell Shares' : 'Close Position') : 
                 showModal === 'assign' ? 'Assignment' : 
                 showModal === 'bulk' ? 'Bulk Add Trades' : 'Import Trades'}
              </h2>
              <button style={styles.modalCloseBtn} onClick={closeModal}><X size={20} /></button>
            </div>
            
            {showModal === 'trade' && (
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Ticker</label>
                    <input style={styles.formInput} value={form.ticker} onChange={e => setForm({ ...form, ticker: e.target.value.toUpperCase() })} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Type</label>
                    <select style={styles.formInput} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="CSP">Cash Secured Put</option>
                      <option value="CC">Covered Call</option>
                      <option value="SHARES">Shares</option>
                    </select>
                  </div>
                  {form.type === 'SHARES' ? (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Quantity</label>
                        <input style={styles.formInput} type="number" min="1" value={form.contracts} onChange={e => setForm({ ...form, contracts: e.target.value })} required />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Cost Basis</label>
                        <input style={styles.formInput} type="number" step="0.01" value={form.strike} onChange={e => setForm({ ...form, strike: e.target.value })} required />
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Strike Price</label>
                        <input style={styles.formInput} type="number" step="0.01" value={form.strike} onChange={e => setForm({ ...form, strike: e.target.value })} required />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Premium</label>
                        <input style={styles.formInput} type="number" step="0.01" value={form.premium} onChange={e => setForm({ ...form, premium: e.target.value })} required />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Contracts</label>
                        <input style={styles.formInput} type="number" min="1" value={form.contracts} onChange={e => setForm({ ...form, contracts: e.target.value })} />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Expiration</label>
                        <input style={styles.formInput} type="date" value={form.expiration} onChange={e => setForm({ ...form, expiration: e.target.value })} />
                      </div>
                    </>
                  )}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Open Date</label>
                    <input style={styles.formInput} type="date" value={form.openDate || today} onChange={e => setForm({ ...form, openDate: e.target.value })} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Fees</label>
                    <input style={styles.formInput} type="number" step="0.01" value={form.entryFees} onChange={e => setForm({ ...form, entryFees: e.target.value })} placeholder="0.00" />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Notes</label>
                  <input style={styles.formInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." />
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
                  <button type="submit" style={styles.primaryButton}>{editingId ? 'Save Changes' : form.type === 'SHARES' ? 'Add Shares' : 'Add Trade'}</button>
                </div>
              </form>
            )}
            
            {showModal === 'shares' && (
              <form onSubmit={handleShareSubmit} style={styles.form}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Ticker</label>
                    <input style={styles.formInput} value={shareForm.ticker} onChange={e => setShareForm({ ...shareForm, ticker: e.target.value.toUpperCase() })} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Quantity</label>
                    <input style={styles.formInput} type="number" min="1" value={shareForm.quantity} onChange={e => setShareForm({ ...shareForm, quantity: e.target.value })} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Cost Basis</label>
                    <input style={styles.formInput} type="number" step="0.01" value={shareForm.costBasis} onChange={e => setShareForm({ ...shareForm, costBasis: e.target.value })} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Date Acquired</label>
                    <input style={styles.formInput} type="date" value={shareForm.openDate || today} onChange={e => setShareForm({ ...shareForm, openDate: e.target.value })} />
                  </div>
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
                  <button type="submit" style={styles.primaryButton}>{editingId ? 'Save Changes' : 'Add Shares'}</button>
                </div>
              </form>
            )}
            
            {showModal === 'close' && modalData && (
              <div style={styles.form}>
                <div style={styles.previewCard}>
                  <div style={styles.previewTicker}>{modalData.ticker}</div>
                  <div style={styles.previewDetails}>
                    {modalData.isShares ? `${modalData.quantity} shares @ $${modalData.costBasis}` : `${modalData.type} $${modalData.strike} × ${modalData.contracts}`}
                  </div>
                </div>
                {closeForm.exitPremium && (
                  <div style={styles.plPreview}>
                    <span style={styles.plPreviewLabel}>Expected P/L</span>
                    <span style={{ ...styles.plPreviewValue, color: previewPL(modalData, closeForm.exitPremium) >= 0 ? colors.profit : colors.loss }}>
                      {fmtMoney(previewPL(modalData, closeForm.exitPremium), true)}
                    </span>
                  </div>
                )}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>{modalData.isShares ? 'Sell Price' : 'Close Price'}</label>
                  <input style={styles.formInput} type="number" step="0.01" value={closeForm.exitPremium} onChange={e => setCloseForm({ ...closeForm, exitPremium: e.target.value })} autoFocus placeholder="0.00" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Fees</label>
                  <input style={styles.formInput} type="number" step="0.01" value={closeForm.exitFees} onChange={e => setCloseForm({ ...closeForm, exitFees: e.target.value })} placeholder="0.00" />
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
                  <button type="button" style={styles.primaryButton} onClick={handleClose}>{modalData.isShares ? 'Sell Shares' : 'Close Position'}</button>
                </div>
              </div>
            )}
            
            {showModal === 'assign' && modalData && (
              <div style={styles.form}>
                <div style={styles.previewCard}>
                  <div style={styles.previewTicker}>{modalData.ticker}</div>
                  <div style={styles.previewDetails}>{modalData.type} ${modalData.strike} × {modalData.contracts}</div>
                </div>
                <div style={styles.assignmentNote}>
                  <Zap size={16} />
                  <span>This will create {modalData.contracts * 100} shares at ${modalData.strike}/share</span>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Assignment Fees</label>
                  <input style={styles.formInput} type="number" step="0.01" value={closeForm.exitFees} onChange={e => setCloseForm({ ...closeForm, exitFees: e.target.value })} autoFocus placeholder="0.00" />
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
                  <button type="button" style={{ ...styles.primaryButton, background: colors.warning, color: colors.bgPrimary }} onClick={handleAssign}>Confirm Assignment</button>
                </div>
              </div>
            )}
            
            {showModal === 'bulk' && (
              <div style={styles.form}>
                <div style={styles.bulkInstructions}>
                  <h4 style={styles.bulkInstructionsTitle}>CSV Format</h4>
                  <p style={styles.bulkInstructionsText}>
                    Each row represents one trade. Use the following columns:
                  </p>
                  <div style={styles.bulkColumns}>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>ticker</span><span style={styles.bulkColumnDesc}>Symbol (e.g., SPY)</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>type</span><span style={styles.bulkColumnDesc}>CSP, CC, or SHARES</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>strike</span><span style={styles.bulkColumnDesc}>Strike price / Cost basis</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>premium</span><span style={styles.bulkColumnDesc}>Premium received</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>contracts</span><span style={styles.bulkColumnDesc}>Number of contracts</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>date</span><span style={styles.bulkColumnDesc}>Open date (YYYY-MM-DD)</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>fees</span><span style={styles.bulkColumnDesc}>Entry fees (optional)</span></div>
                    <div style={styles.bulkColumn}><span style={styles.bulkColumnName}>status</span><span style={styles.bulkColumnDesc}>open, closed, expired, assigned</span></div>
                  </div>
                </div>
                <div style={styles.bulkExample}>
                  <span style={styles.bulkExampleLabel}>Example:</span>
                  <code style={styles.bulkExampleCode}>SPY,CSP,450,1.25,2,2024-01-15,0.65,open</code>
                </div>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".csv,.txt" onChange={e => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = ev => setBulkText(ev.target.result); r.readAsText(f); } }} />
                <button style={styles.uploadButton} onClick={() => fileInputRef.current?.click()}>
                  <Download size={18} />
                  <span>Upload CSV File</span>
                </button>
                <div style={styles.bulkDivider}>
                  <span style={styles.bulkDividerLine}></span>
                  <span style={styles.bulkDividerText}>or paste data</span>
                  <span style={styles.bulkDividerLine}></span>
                </div>
                <textarea style={styles.textarea} placeholder="Paste your CSV data here..." value={bulkText} onChange={e => setBulkText(e.target.value)} rows={6} />
                {bulkText.trim() && (
                  <div style={styles.bulkPreview}>
                    <span style={styles.bulkPreviewIcon}>✓</span>
                    <span>{bulkText.trim().split('\n').filter(l => l.trim() && !l.toLowerCase().includes('ticker')).length} trades ready to import</span>
                  </div>
                )}
                <div style={styles.formActions}>
                  <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
                  <button type="button" style={styles.primaryButton} onClick={handleBulkUpload} disabled={!bulkText.trim()}>
                    <Plus size={16} />
                    Import Trades
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Roll Modal */}
      {showRollModal && (
        <div style={styles.modalOverlay} onClick={() => { setShowRollModal(null); setRollForm({ strike: '', premium: '', expiration: '', contracts: 1 }); setCloseForm({ exitPremium: '', exitFees: '' }); }}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Roll Position</h2>
              <button style={styles.modalCloseBtn} onClick={() => { setShowRollModal(null); setRollForm({ strike: '', premium: '', expiration: '', contracts: 1 }); setCloseForm({ exitPremium: '', exitFees: '' }); }}><X size={20} /></button>
            </div>
            <div style={styles.previewCard}>
              <div style={styles.previewTicker}>{showRollModal.ticker}</div>
              <div style={styles.previewDetails}>{showRollModal.type} ${showRollModal.strike} @ ${showRollModal.premium}</div>
            </div>
            
            <div style={styles.rollSection}>
              <div style={styles.rollSectionLabel}>Close Current Position</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Buy to Close</label>
                  <input style={styles.formInput} type="number" step="0.01" value={closeForm.exitPremium} onChange={e => setCloseForm({ ...closeForm, exitPremium: e.target.value })} placeholder="0.00" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Fees</label>
                  <input style={styles.formInput} type="number" step="0.01" value={closeForm.exitFees} onChange={e => setCloseForm({ ...closeForm, exitFees: e.target.value })} placeholder="0.00" />
                </div>
              </div>
            </div>
            
            <div style={styles.rollSection}>
              <div style={styles.rollSectionLabel}>Open New Position</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>New Strike</label>
                  <input style={styles.formInput} type="number" step="0.01" value={rollForm.strike} onChange={e => setRollForm({ ...rollForm, strike: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>New Premium</label>
                  <input style={styles.formInput} type="number" step="0.01" value={rollForm.premium} onChange={e => setRollForm({ ...rollForm, premium: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Expiration</label>
                  <input style={styles.formInput} type="date" value={rollForm.expiration} onChange={e => setRollForm({ ...rollForm, expiration: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Contracts</label>
                  <input style={styles.formInput} type="number" min="1" value={rollForm.contracts} onChange={e => setRollForm({ ...rollForm, contracts: e.target.value })} />
                </div>
              </div>
            </div>
            
            {rollForm.premium && closeForm.exitPremium !== '' && (
              <div style={styles.rollNetCredit}>
                <span style={styles.rollNetCreditLabel}>Net Credit</span>
                <span style={{ ...styles.rollNetCreditValue, color: (parseFloat(rollForm.premium) - parseFloat(closeForm.exitPremium || 0)) >= 0 ? colors.profit : colors.loss }}>
                  {fmtMoney((parseFloat(rollForm.premium) - parseFloat(closeForm.exitPremium || 0)) * (parseInt(rollForm.contracts) || showRollModal.contracts) * 100, true)}
                </span>
              </div>
            )}
            
            <div style={styles.formActions}>
              <button type="button" style={styles.secondaryButton} onClick={() => { setShowRollModal(null); setRollForm({ strike: '', premium: '', expiration: '', contracts: 1 }); setCloseForm({ exitPremium: '', exitFees: '' }); }}>Cancel</button>
              <button type="button" style={{ ...styles.primaryButton, background: colors.purple }} onClick={handleRoll} disabled={!rollForm.strike || !rollForm.premium}>Roll Position</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div style={styles.modalOverlay} onClick={() => setShowImportModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 900, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Import Trades</h2>
              <button style={styles.modalCloseBtn} onClick={() => setShowImportModal(false)}><X size={20} /></button>
            </div>
            
            <div style={{ padding: '0 24px 16px', borderBottom: `1px solid ${colors.borderSubtle}` }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: colors.textSecondary, fontSize: 14 }}>
                  {importData.length} trades detected • {importSelected.size} selected
                </span>
                <button 
                  style={{ ...styles.secondaryButton, padding: '6px 12px', fontSize: 12 }} 
                  onClick={toggleAllImportRows}
                >
                  {importSelected.size === importData.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '0 24px' }}>
              <table style={{ ...styles.table, marginTop: 16 }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 40 }}></th>
                    <th style={styles.th}>Action</th>
                    <th style={styles.th}>Symbol</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Strike</th>
                    <th style={styles.th}>Price</th>
                    <th style={styles.th}>Qty</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Match</th>
                  </tr>
                </thead>
                <tbody>
                  {importData.map((row, idx) => {
                    const isSelected = importSelected.has(idx);
                    const actionLabel = {
                      'sell_to_open': 'Sell to Open',
                      'buy_to_close': 'Buy to Close',
                      'assigned': 'Assigned',
                      'expired': 'Expired',
                      'buy_stock': 'Buy Stock',
                      'sell_stock': 'Sell Stock',
                    }[row.action] || row.action;
                    
                    const actionColor = {
                      'sell_to_open': colors.profit,
                      'buy_to_close': colors.loss,
                      'assigned': colors.warning,
                      'expired': colors.profit,
                      'buy_stock': colors.accent,
                      'sell_stock': colors.profit,
                    }[row.action] || colors.textSecondary;
                    
                    return (
                      <tr key={idx} style={{ ...styles.tr, opacity: isSelected ? 1 : 0.4 }}>
                        <td style={styles.td}>
                          <button style={styles.checkboxButton} onClick={() => toggleImportRow(idx)}>
                            {isSelected ? <CheckSquare size={16} style={{ color: colors.accent }} /> : <Square size={16} />}
                          </button>
                        </td>
                        <td style={{ ...styles.td, color: actionColor, fontWeight: 500 }}>{actionLabel}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{row.symbol}</td>
                        <td style={styles.td}>
                          {row.type && (
                            <span style={{ 
                              ...styles.typeBadge, 
                              background: row.type === 'CSP' ? colors.accentMuted : colors.purpleMuted, 
                              color: row.type === 'CSP' ? colors.accent : colors.purple 
                            }}>
                              {row.type}
                            </span>
                          )}
                        </td>
                        <td style={styles.td}>{row.strike ? `$${row.strike.toFixed(2)}` : '—'}</td>
                        <td style={styles.td}>${row.price.toFixed(2)}</td>
                        <td style={styles.td}>{row.quantity}</td>
                        <td style={styles.td}>{row.date}</td>
                        <td style={styles.td}>
                          {row.matchingTrade ? (
                            <span style={{ color: colors.profit, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Link2 size={12} /> Matched
                            </span>
                          ) : (row.action === 'buy_to_close' || row.action === 'assigned') ? (
                            <span style={{ color: colors.warning }}>No match</span>
                          ) : (
                            <span style={{ color: colors.textTertiary }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ padding: 24, borderTop: `1px solid ${colors.borderSubtle}`, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button style={styles.secondaryButton} onClick={() => setShowImportModal(false)}>Cancel</button>
              <button 
                style={styles.primaryButton} 
                onClick={handleImportConfirm}
                disabled={importSelected.size === 0}
              >
                Import {importSelected.size} Trade{importSelected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for import */}
      <input
        type="file"
        ref={importFileRef}
        style={{ display: 'none' }}
        accept=".csv,.txt,.html,.htm"
        onChange={handleImportFile}
      />

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div style={styles.modalOverlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...styles.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={styles.confirmIcon}>
              <Trash2 size={24} />
            </div>
            <h2 style={styles.confirmTitle}>{confirmDelete === 'bulk' ? `Delete ${selectedTrades.size} trades?` : 'Delete this trade?'}</h2>
            <p style={styles.confirmText}>This action cannot be undone.</p>
            <div style={styles.formActions}>
              <button style={styles.secondaryButton} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={{ ...styles.primaryButton, background: colors.loss }} onClick={() => confirmDelete === 'bulk' ? handleBulkDelete() : deleteTrade(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  // Container
  container: {
    minHeight: '100vh',
    background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)`,
    color: colors.textPrimary,
    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '24px 32px',
    maxWidth: 1400,
    margin: '0 auto',
  },

  // Login Screen
  loginContainer: {
    minHeight: '100vh',
    background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loginCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 24,
    padding: '48px 40px',
    textAlign: 'center',
    maxWidth: 400,
    width: '100%',
    margin: '0 20px',
  },
  loginLogoIcon: {
    width: 80,
    height: 80,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    color: 'white',
    boxShadow: `0 8px 32px ${colors.accent}40`,
  },
  loginTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.textPrimary,
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  loginSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    margin: '0 0 32px 0',
  },
  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 24px',
    background: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    color: '#1f2937',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  loginNote: {
    fontSize: 13,
    color: colors.textTertiary,
    marginTop: 24,
  },

  // User Menu
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 8,
    paddingLeft: 12,
    borderLeft: `1px solid ${colors.borderSubtle}`,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: `2px solid ${colors.borderDefault}`,
  },
  userAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: colors.bgTertiary,
    border: `2px solid ${colors.borderDefault}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textSecondary,
  },
  signOutButton: {
    width: 36,
    height: 36,
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { display: 'flex', alignItems: 'center', gap: 14 },
  logoIcon: {
    width: 48,
    height: 48,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 8px 32px ${colors.accent}40`,
  },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoTitle: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' },
  logoSubtitle: { fontSize: 13, color: colors.textTertiary, fontWeight: 500 },
  headerButton: {
    width: 42,
    height: 42,
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 12,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
  },
  cloudStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  cloudStatusText: {
    fontSize: 12,
    fontWeight: 600,
  },
  secondaryHeaderButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 18px',
    background: colors.bgCard,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 12,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flex: 1,
    padding: '14px 20px',
    background: colors.accent,
    border: 'none',
    borderRadius: 12,
    color: colors.bgPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 20px ${colors.accent}40`,
  },
  secondaryButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 20px',
    background: colors.bgPrimary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 12,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 20,
    padding: 24,
    transition: 'all 0.3s ease',
  },
  statHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  statLabel: {
    fontSize: 14,
    color: colors.textTertiary,
    fontWeight: 500,
  },
  statIcon: {
    width: 36,
    height: 36,
    background: colors.bgTertiary,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textSecondary,
  },
  statValue: {
    fontSize: 32,
    fontWeight: 700,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    marginBottom: 8,
  },
  statFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: colors.textTertiary,
  },

  // Quick Entry
  quickEntryButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 18,
    background: colors.bgCard,
    border: `1px dashed ${colors.borderDefault}`,
    borderRadius: 16,
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: 24,
  },
  keyboardShortcut: {
    padding: '4px 8px',
    background: colors.bgTertiary,
    borderRadius: 6,
    fontSize: 11,
    fontFamily: 'monospace',
    color: colors.textTertiary,
  },
  quickEntryCard: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
  },
  quickEntryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  quickEntryTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  quickFavorites: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  favoriteChip: {
    padding: '8px 14px',
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 8,
    color: colors.textSecondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  favoriteChipActive: {
    background: colors.accentMuted,
    borderColor: colors.accent,
    color: colors.accent,
  },
  quickEntryRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  quickInput: {
    width: 100,
    padding: '12px 14px',
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  quickSelect: {
    padding: '12px 14px',
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    outline: 'none',
  },
  quickSubmitBtn: {
    width: 44,
    height: 44,
    background: colors.profit,
    border: 'none',
    borderRadius: 10,
    color: colors.bgPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  quickCancelBtn: {
    width: 44,
    height: 44,
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 10,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  // Toolbar
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 16,
  },
  filterGroup: {
    display: 'flex',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 12,
    padding: 4,
  },
  filterButton: {
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    color: colors.textTertiary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    background: colors.bgTertiary,
    color: colors.textPrimary,
  },
  toolbarRight: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  toolbarButton: {
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    color: colors.textSecondary,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  selectedCount: {
    color: colors.accent,
    fontSize: 13,
    fontWeight: 600,
  },
  searchContainer: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.textTertiary,
  },
  searchInput: {
    width: 160,
    padding: '10px 14px 10px 36px',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 13,
    outline: 'none',
  },
  sortSelect: {
    padding: '10px 14px',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 13,
    cursor: 'pointer',
    outline: 'none',
  },
  viewToggle: {
    display: 'flex',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  viewButton: {
    width: 40,
    height: 40,
    background: 'transparent',
    border: 'none',
    color: colors.textTertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  viewButtonActive: {
    background: colors.bgTertiary,
    color: colors.textPrimary,
  },
  moreButton: {
    width: 40,
    height: 40,
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  // Grouped View
  groupedContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  tickerGroup: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 16,
    overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: colors.bgSecondary,
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  groupHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  groupTicker: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.textPrimary,
  },
  groupCount: {
    fontSize: 13,
    color: colors.textTertiary,
    background: colors.bgTertiary,
    padding: '4px 10px',
    borderRadius: 20,
  },
  groupHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  groupStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  groupStatLabel: {
    fontSize: 11,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  groupStatValue: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  groupTrades: {
    padding: '8px 0',
  },
  groupDivider: {
    height: 1,
    background: colors.borderSubtle,
    margin: '8px 20px',
  },
  groupTradeRow: {
    display: 'grid',
    gridTemplateColumns: '70px 80px 70px 50px 70px 50px 80px 90px 1fr',
    alignItems: 'center',
    padding: '10px 20px',
    gap: 8,
    transition: 'background 0.15s ease',
  },
  groupTradeType: {
    display: 'flex',
    alignItems: 'center',
  },
  groupTypeBadge: {
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  groupTradeStrike: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  groupTradePremium: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  groupTradeQty: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  groupTradeExp: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  groupTradeDTE: {
    display: 'flex',
    justifyContent: 'center',
  },
  dteBadgeSmall: {
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
  },
  groupTradeStatus: {
    display: 'flex',
    justifyContent: 'center',
  },
  statusBadgeSmall: {
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
  },
  groupTradePL: {
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'right',
  },
  groupTradeActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 4,
  },
  groupActionBtn: {
    background: 'transparent',
    border: 'none',
    color: colors.textTertiary,
    padding: 6,
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },

  // Cards Grid
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  tradeCard: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 20,
    padding: 20,
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  tradeCardSelected: {
    borderColor: colors.accent,
    background: `${colors.accent}10`,
  },
  tradeCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  tradeCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  tradeCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  checkboxButton: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    color: colors.textTertiary,
  },
  tradeTicker: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  typeBadge: {
    padding: '6px 10px',
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  dteBadge: {
    padding: '6px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
  },
  rollIndicator: {
    width: 28,
    height: 28,
    background: colors.purpleMuted,
    border: 'none',
    borderRadius: 8,
    color: colors.purple,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  tradeCardBody: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: 12,
    marginBottom: 16,
    padding: '16px 0',
    borderTop: `1px solid ${colors.borderSubtle}`,
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  tradeField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  tradeFieldLabel: {
    fontSize: 11,
    color: colors.textTertiary,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  tradeFieldValue: {
    fontSize: 15,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  tradeCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
  },
  tradePL: {
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  tradeCardActions: {
    display: 'flex',
    gap: 4,
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${colors.borderSubtle}`,
  },
  actionButton: {
    flex: 1,
    padding: '10px',
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Table
  tableWrapper: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 20,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px 20px',
    fontSize: 12,
    fontWeight: 600,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${colors.borderSubtle}`,
    background: colors.bgTertiary,
  },
  tr: {
    borderBottom: `1px solid ${colors.borderSubtle}`,
    transition: 'all 0.2s ease',
  },
  trSelected: {
    background: `${colors.accent}10`,
  },
  td: {
    padding: '16px 20px',
    fontSize: 14,
    fontVariantNumeric: 'tabular-nums',
  },
  tdTicker: {
    fontWeight: 700,
    fontSize: 15,
  },
  tableActions: {
    display: 'flex',
    gap: 6,
    justifyContent: 'flex-end',
  },

  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 24,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    background: colors.bgTertiary,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    color: colors.textTertiary,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 8,
    color: colors.textPrimary,
  },
  emptyText: {
    fontSize: 15,
    color: colors.textTertiary,
    marginBottom: 24,
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: `linear-gradient(145deg, ${colors.bgSecondary} 0%, ${colors.bgTertiary} 100%)`,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 20,
    padding: '28px 32px 32px 32px',
    width: 480,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: 'calc(100vh - 40px)',
    overflow: 'auto',
    boxShadow: '0 32px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
    boxSizing: 'border-box',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 20,
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    margin: 0,
    color: colors.textPrimary,
  },
  modalCloseBtn: {
    width: 36,
    height: 36,
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Form
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    rowGap: 18,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  formInput: {
    padding: '12px 14px',
    background: colors.bgPrimary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    outline: 'none',
    transition: 'all 0.2s ease',
    width: '100%',
    boxSizing: 'border-box',
  },
  formActions: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
    paddingTop: 20,
    borderTop: `1px solid ${colors.borderSubtle}`,
  },

  // Preview Card
  previewCard: {
    background: colors.bgTertiary,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
  },
  previewTicker: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 4,
  },
  previewDetails: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  plPreview: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: colors.bgTertiary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  plPreviewLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  plPreviewValue: {
    fontSize: 20,
    fontWeight: 700,
  },
  assignmentNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: colors.warningMuted,
    borderRadius: 12,
    padding: 16,
    color: colors.warning,
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 20,
  },

  // Bulk Upload
  bulkInstructions: {
    background: colors.bgPrimary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  bulkInstructionsTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.textPrimary,
    margin: '0 0 8px 0',
  },
  bulkInstructionsText: {
    fontSize: 13,
    color: colors.textSecondary,
    margin: '0 0 12px 0',
  },
  bulkColumns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  bulkColumn: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 10px',
    background: colors.bgTertiary,
    borderRadius: 6,
    fontSize: 12,
  },
  bulkColumnName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: colors.accent,
  },
  bulkColumnDesc: {
    color: colors.textTertiary,
  },
  bulkExample: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    background: colors.bgPrimary,
    borderRadius: 10,
    marginBottom: 16,
  },
  bulkExampleLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textTertiary,
  },
  bulkExampleCode: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: colors.profit,
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 18,
    background: colors.accentMuted,
    border: `2px dashed ${colors.accent}`,
    borderRadius: 12,
    color: colors.accent,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
  },
  bulkDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '16px 0',
  },
  bulkDividerLine: {
    flex: 1,
    height: 1,
    background: colors.borderSubtle,
  },
  bulkDividerText: {
    fontSize: 12,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  textarea: {
    padding: 14,
    background: colors.bgPrimary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 13,
    fontFamily: 'monospace',
    minHeight: 120,
    resize: 'vertical',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  bulkPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    background: colors.profitMuted,
    borderRadius: 10,
    marginTop: 12,
    color: colors.profit,
    fontSize: 13,
    fontWeight: 600,
  },
  bulkPreviewIcon: {
    fontSize: 16,
  },

  // Confirm Delete
  confirmIcon: {
    width: 64,
    height: 64,
    background: colors.lossMuted,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    color: colors.loss,
  },
  confirmTitle: {
    fontSize: 20,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 8,
  },
  confirmText: {
    fontSize: 14,
    color: colors.textTertiary,
    textAlign: 'center',
    marginBottom: 24,
  },

  // Roll Modal
  rollSection: {
    marginBottom: 20,
  },
  rollSectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 12,
  },
  rollNetCredit: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: colors.bgTertiary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  rollNetCreditLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  rollNetCreditValue: {
    fontSize: 20,
    fontWeight: 700,
  },

  // Roll History
  rollSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: colors.bgTertiary,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
  },
  rollSummaryLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  rollSummaryValue: {
    fontSize: 28,
    fontWeight: 700,
  },
  rollTimeline: {
    display: 'flex',
    flexDirection: 'column',
  },
  rollItem: {
    position: 'relative',
    paddingLeft: 28,
    paddingBottom: 20,
  },
  rollDot: {
    position: 'absolute',
    left: 0,
    top: 6,
    width: 14,
    height: 14,
    background: colors.purple,
    borderRadius: '50%',
    border: `3px solid ${colors.bgSecondary}`,
  },
  rollLine: {
    position: 'absolute',
    left: 6,
    top: 24,
    bottom: 0,
    width: 2,
    background: colors.purpleMuted,
  },
  rollContent: {
    background: colors.bgTertiary,
    borderRadius: 14,
    padding: 16,
  },
  rollHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  rollType: {
    fontSize: 15,
    fontWeight: 700,
  },
  rollMeta: {
    display: 'flex',
    gap: 16,
    fontSize: 13,
    color: colors.textTertiary,
  },
  rollPL: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: 700,
  },

  // Page Views
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
  },

  // Calendar
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  monthLabel: {
    fontSize: 18,
    fontWeight: 700,
  },
  navButton: {
    width: 40,
    height: 40,
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 10,
    color: colors.textPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 8,
  },
  calendarDayHeader: {
    textAlign: 'center',
    padding: 12,
    fontSize: 12,
    fontWeight: 600,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  calendarCell: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 12,
    padding: 10,
    minHeight: 90,
  },
  calendarToday: {
    borderColor: colors.accent,
    boxShadow: `0 0 0 1px ${colors.accent}`,
  },
  calendarDayNum: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textSecondary,
    marginBottom: 6,
    display: 'block',
  },
  calendarTrade: {
    fontSize: 10,
    fontWeight: 700,
    padding: '4px 6px',
    borderRadius: 4,
    marginBottom: 4,
  },
  calendarMore: {
    fontSize: 10,
    color: colors.textTertiary,
    fontWeight: 500,
  },
  legendRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    marginTop: 24,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: colors.textSecondary,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },

  // Analytics
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  metricCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 16,
    padding: 20,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  metricIcon: {
    width: 44,
    height: 44,
    background: colors.bgTertiary,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textSecondary,
  },
  metricContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  metricLabel: {
    fontSize: 13,
    color: colors.textTertiary,
    fontWeight: 500,
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 700,
  },
  sectionHeader: {
    marginTop: 32,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    margin: 0,
  },
  chartContainer: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 20,
    padding: 24,
  },
  pieLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 20,
  },
  pieLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 14,
  },
  pieLegendTicker: {
    fontWeight: 600,
  },
  pieLegendValue: {
    marginLeft: 'auto',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },

  // Dashboard
  heroCard: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 24,
    padding: 40,
    textAlign: 'center',
    marginBottom: 24,
  },
  heroLabel: {
    fontSize: 14,
    color: colors.textTertiary,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  heroValue: {
    fontSize: 56,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    marginBottom: 8,
  },
  heroSubValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 15,
  },
  periodSelector: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 24,
  },
  periodButton: {
    padding: '10px 18px',
    background: 'transparent',
    border: 'none',
    borderRadius: 10,
    color: colors.textTertiary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  periodButtonActive: {
    background: colors.bgCard,
    color: colors.accent,
  },
  dashboardTabs: {
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 24,
    background: colors.bgSecondary,
    borderRadius: 12,
    padding: 4,
  },
  dashboardTab: {
    padding: '10px 24px',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    color: colors.textTertiary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  dashboardTabActive: {
    background: colors.bgCard,
    color: colors.textPrimary,
  },
  enhancedStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 12,
    marginBottom: 24,
  },
  enhancedStatCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 12,
    padding: 16,
    textAlign: 'center',
  },
  enhancedStatLabel: {
    fontSize: 12,
    color: colors.textTertiary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  enhancedStatValue: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.textPrimary,
    fontVariantNumeric: 'tabular-nums',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    padding: '0 8px',
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.textSecondary,
  },
  chartValue: {
    fontSize: 18,
    fontWeight: 700,
  },
  quickStatsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginTop: 24,
    padding: 16,
    background: colors.bgCard,
    borderRadius: 12,
    border: `1px solid ${colors.borderSubtle}`,
  },
  quickStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  quickStatLabel: {
    fontSize: 11,
    color: colors.textTertiary,
    textTransform: 'uppercase',
  },
  quickStatValue: {
    fontSize: 16,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  dailyLogContainer: {
    marginTop: 8,
  },
  weeklyContainer: {
    marginTop: 8,
  },
  emptyState: {
    textAlign: 'center',
    padding: 48,
    color: colors.textTertiary,
  },
  weekNav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  weekLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 15,
    fontWeight: 600,
  },
  currentBadge: {
    padding: '4px 10px',
    background: colors.accentMuted,
    borderRadius: 6,
    color: colors.accent,
    fontSize: 11,
    fontWeight: 700,
  },
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 12,
    marginBottom: 16,
  },
  weekDayCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 14,
    padding: 16,
    textAlign: 'center',
  },
  weekDayName: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 8,
  },
  weekDayPL: {
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  weekDayTrades: {
    fontSize: 11,
    color: colors.textTertiary,
    marginTop: 4,
  },
  weekTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 14,
    padding: 20,
  },
  weekTotalLabel: {
    fontSize: 15,
    fontWeight: 600,
  },
  weekTotalValue: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },

  // Tooltip
  tooltip: {
    background: colors.bgElevated,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 12,
    padding: 14,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  tooltipDate: {
    fontSize: 12,
    color: colors.textTertiary,
    marginBottom: 4,
  },
  tooltipValue: {
    fontSize: 20,
    fontWeight: 700,
  },
  tooltipChange: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 4,
  },
};

export default WheelTracker;